/**
 * Fold the expanded/collapsed state into a branch's accessible name.
 *
 * aria-expanded is not a supported state on role="radio", so a screen reader never
 * reports it there. Putting the state in the name means it is announced every time
 * the branch is, which is far more robust than relying on a live region alone.
 */
function wdSetBranchStateInName(el, isExpanded) {
  if (!el || !el.dataset) return;
  var base = el.dataset.name || el.getAttribute('aria-label') || '';
  // Strip any state we appended previously so it never accumulates.
  base = base.replace(/,\s*(מורחב|מכווץ|expanded|collapsed)\s*$/i, '').trim();
  var expandedText = (window.SDG && SDG.Data && SDG.Data.a11yExpanded) || 'מורחב';
  var collapsedText = (window.SDG && SDG.Data && SDG.Data.a11yCollapsed) || 'מכווץ';
  el.setAttribute('aria-label', base + ', ' + (isExpanded ? expandedText : collapsedText));
}

/**
 * Announce an expand/collapse change to screen readers.
 *
 * NVDA reports aria-expanded only at the moment an element receives focus, and it
 * ignores the attribute entirely on role="radio" (radio supports aria-checked, not
 * aria-expanded). Both were verified against NVDA directly. So toggling the branch
 * details silently produced no feedback at all. A polite live region is the one
 * channel that reliably announces the change while focus stays put.
 *
 * The region must live inside the same dialog as the control: the store drawer is
 * role="dialog" aria-modal="true", and a modal hides everything outside itself from
 * assistive technology, so a region on document.body is written but never heard.
 * Pass the toggled element so the region can be placed in the right subtree.
 */
function wdAnnounceExpanded(isExpanded, contextEl) {
  var host = document.body;
  if (contextEl && contextEl.closest) {
    host = contextEl.closest('[aria-modal="true"], [role="dialog"]') || document.body;
  }
  var region = host.querySelector(':scope > .wd-a11y-status');
  if (!region) {
    region = document.createElement('div');
    region.className = 'wd-a11y-status visually-hidden';
    // Assertive, not polite: selecting a branch also flips aria-checked, and NVDA
    // speaking that state discards a queued polite message. Verified against NVDA:
    // the polite region is announced fine on its own, but not alongside that speech.
    // No role="alert" though - NVDA speaks that role name aloud, so the user hears
    // a literal "alert" before the text. aria-live alone gives the urgency silently.
    region.setAttribute('aria-live', 'assertive');
    region.setAttribute('aria-atomic', 'true');
    host.appendChild(region);
  }
  var expandedText = (window.SDG && SDG.Data && SDG.Data.a11yExpanded) || 'מורחב';
  var collapsedText = (window.SDG && SDG.Data && SDG.Data.a11yCollapsed) || 'מכווץ';
  // Clear first, otherwise repeating the same state is not re-announced. The delay
  // also gives a freshly created region time to be picked up before it changes;
  // setTimeout rather than requestAnimationFrame, which does not fire when hidden.
  region.textContent = '';
  // Long enough for the radio's own "checked" announcement to finish first, so the
  // two do not collide; assertive then guarantees this one is still spoken.
  setTimeout(function () {
    region.textContent = isExpanded ? expandedText : collapsedText;
  }, 350);
}

/** Line-item pickup inside mini cart drawers: exclude from store-locator queries/renders; full /cart page still included. */
function wdIsPickupInsideCartDrawer(el) {
  if (!el || typeof el.closest !== 'function') return false;
  return Boolean(
    el.closest('#CartDrawer') ||
    el.closest('cart-drawer') ||
    el.closest('#CTOCartDrawer') ||
    el.closest('cto-cart-drawer')
  );
}

/** @param {string} selector passed to querySelectorAll */
function wdPickupAvailabilitiesOutsideCartDrawer(selector) {
  return [...document.querySelectorAll(selector)].filter((el) => !wdIsPickupInsideCartDrawer(el));
}

/** Hebrew substring used in location display names for Eilat branches */
const WD_EILAT_LOCATION_NAME_MARK = 'אילת';

/**
 * Apply Eilat branch name filter for pickup / header drawer everywhere except the full store-locator map page.
 * (Previously only /cart and /products/* — pickup from cart drawer on home/collection skipped filtering.)
 */
function wdStoreLocatorDrawerShouldFilterByEilatPage() {
  const path = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  return (path.includes('/cart')||path.includes('/products/'))? true : false;
}

/**
 * Cart / handler / storage before body class: a stale `eilatMode` on body after leaving Eilat
 * must not keep the locator on the Eilat-only list (body was wrongly checked first before).
 */
function wdStoreLocatorDrawerIsEilatMode() {
  if (typeof window.Eilat_handler?.isEilatMode_active === 'function') {
    try {
      const v = window.Eilat_handler.isEilatMode_active();
      if (v === true) return true;
      if (v === false) return false;
    } catch (_) {}
  }
  try {
    const s = sessionStorage.getItem('eilatMode');
    if (s === 'true') return true;
    if (s === 'false') return false;
  } catch (_) {}
  return Boolean(
    typeof document !== 'undefined' && document.body && document.body.classList.contains('eilatMode')
  );
}

function wdLocationMatchesEilatCartMode(location, eilatModeEnabled) {
  const name = location?.name || '';
  const isEilatBranch = name.includes(WD_EILAT_LOCATION_NAME_MARK);
  return eilatModeEnabled ? isEilatBranch : !isEilatBranch;
}

/** First location valid for cart Eilat mode (name + checkoutEnabled when metafield present). */
function wdFirstLocationForEilatCartMode(locations, eilatModeEnabled) {
  if (!locations || !locations.length) return null;
  const matchesMode = (loc) => wdLocationMatchesEilatCartMode(loc, eilatModeEnabled);
  const withCheckout = locations.find((loc) => {
    if (!matchesMode(loc)) return false;
    const toggles = JSON.parse(loc.enableToggles?.value || null);
    if (toggles?.checkoutEnabled === false) return false;
    return true;
  });
  if (withCheckout) return withCheckout;
  return locations.find(matchesMode) || null;
}

if (!customElements.get('my-store-locator')) {
  customElements.define(
    'my-store-locator',
    class MyStoreLocator extends HTMLElement {
      constructor() {
        super();

        this.dom = {
          mobile: 'my-store-locator-mobile',
          btn: '.js-my-store-locator-btn',
          drawer: 'my-store-locator-drawer',
          drawerBtn: '.js-my-store-locator-drawer-btn',
          info: '.js-my-store-locator-info',
          details: '.js-my-store-locator-details',
          close: '.js-my-store-locator-close',
          location: '.js-my-store-locator-location',
          telephone: '.js-my-store-locator-telephone',
          email: '.js-my-store-locator-email',
          address: '.js-my-store-locator-address',
          hours: '.js-my-store-locator-hours',
          services: '.js-my-store-locator-services',
          distance: '.js-my-store-locator-distance',
          pickupAvailability: 'pickup-availability',
          searchButton: '.js-my-store-locator-search-drawer-launcher',
          filterResults: 'ProductGridContainer',
          filterTag: '.js-my-store-pickup-location-text',
          filterInput: '.js-my-store-pickup-location-input',
          storeLocatorSearch: 'storeLocatorSearch',
        };

        this.mobileLocator = document.querySelector(this.dom.mobile);
        this.button = this.querySelector(this.dom.btn);
        this.drawer = document.querySelector(this.dom.drawer);
        this.drawerBtn = this.querySelector(this.dom.drawerBtn);
        if (this.drawerBtn) this.drawerBtn.addEventListener('click', this.onClickShowStoreSelector.bind(this));
        this.details = this.querySelector(this.dom.details);
        this.detailsClose = this.details.querySelector(this.dom.close);
        this.detailsClose.addEventListener('click', this.hideDetails.bind(this));
        this.detailsLocation = this.details.querySelector(this.dom.location);
        this.detailsTelephone = this.details.querySelector(this.dom.telephone);
        this.detailsEmail = this.details.querySelector(this.dom.email);
        this.detailsAddress = this.details.querySelector(this.dom.address);
        this.detailsHours = this.details.querySelector(this.dom.hours);
        this.detailsServices = this.details.querySelector(this.dom.services);
        this.distance = this.details.querySelector(this.dom.distance);
        this.filterResults = document.getElementById(this.dom.filterResults) || null;
        this.storeLocatorSearch = document.getElementById(this.dom.storeLocatorSearch) || null;


        this.onBodyClick = this.handleBodyClick.bind(this);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onClickToggleDetails = this.onClickToggleDetails.bind(this);
        this.onClickShowStoreSelector = this.onClickShowStoreSelector.bind(this);

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hideDetails();
        });

        if (this?.filterResults) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {

              if (mutation?.type === 'childList') {
                const $searchButtons = document.querySelectorAll(this.dom.searchButton);
                $searchButtons.forEach(($searchButton) => {
                  $searchButton.addEventListener('click', this.onClickShowStoreSelector.bind(this));
                });

                this.updateFacets(this.button.querySelector(this.dom.info).innerHTML);
              }
            });
          });

          // watch for changes to the filters and add the event listeners
          observer.observe(this.filterResults, { childList: true });
        }

        const $searchButtons = document.querySelectorAll(this.dom.searchButton);
        $searchButtons.forEach(($searchButton) => {
          $searchButton.addEventListener('click', this.onClickShowStoreSelector.bind(this));
        });

        this.preferredStoreId = null;
        this.myCoords = null;
        this.allLocationsData = [];
        this.itemLocationsData = {};
        this.storeSuggestions = [];

        this.expiry = SDG.Data.locationCookieExpiry || 30;

        this.init();

        window.refreshStoreLocator = this.init.bind(this);

        this._wdOnEilatPreferredSync = (ev) => {
          const enabled = Boolean(ev?.detail?.enabled);
          const sync = this.wdReassignPreferredStoreForEilatMode(enabled, {
            skipCartAttributes: Boolean(ev?.detail?.skipLocationCartUpdate),
          });
          if (typeof ev?.detail?.waitFor === 'function') ev.detail.waitFor(sync);
        };
        window.addEventListener('wd:eilat-mode-changed', this._wdOnEilatPreferredSync);
      }

      disconnectedCallback() {
        if (this._wdOnEilatPreferredSync) {
          window.removeEventListener('wd:eilat-mode-changed', this._wdOnEilatPreferredSync);
        }
      }

      /**
       * Location that should become preferred for the given Eilat cart mode, or null if current is already valid / none.
       * @param {boolean} eilatModeEnabled
       */
      wdPeekPreferredStoreForEilatMode(eilatModeEnabled) {
        if (!this.allLocationsData || !this.allLocationsData.length) return null;

        let preferredId = this.preferredStoreId;
        if (!preferredId && this.storageAvailable('localStorage')) {
          preferredId = localStorage.getItem('preferredStoreId');
        }
        if (!preferredId) return null;

        const current = this.allLocationsData.find((l) => l.id === preferredId);
        if (current && wdLocationMatchesEilatCartMode(current, eilatModeEnabled)) return null;

        return wdFirstLocationForEilatCartMode(this.allLocationsData, eilatModeEnabled) || null;
      }

      /**
       * If favorite store is not allowed for current Eilat cart mode, switch to the first allowed location.
       * @param {boolean} eilatModeEnabled - same as Eilat_handler / wd:eilat-mode-changed detail.enabled
       * @param {{ skipCartAttributes?: boolean }} [opts] - when true, only update UI/localStorage (cart attrs already set elsewhere)
       */
      wdReassignPreferredStoreForEilatMode(eilatModeEnabled, opts = {}) {
        const replacement = this.wdPeekPreferredStoreForEilatMode(eilatModeEnabled);
        if (!replacement) return;

        if (this.storageAvailable('localStorage')) {
          localStorage.setItem('preferredStoreId', replacement.id);
        }
        return this.updateMyStoreWithLocation(replacement, {
          skipCartAttributes: Boolean(opts.skipCartAttributes),
        });
      }

      setSecureCookie(name, value, days) {
        var expires = "";

        if (days) {
          var date = new Date();
          date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
          expires = "; expires=" + date.toUTCString();
        }
        document.cookie = name + "=" + (value || "") + expires + "; path=/" + "; secure";
      }

      getCookie(name) {
        var nameEQ = name + "=";
        var ca = document.cookie.split(';');

        for (var i = 0; i < ca.length; i++) {
          var c = ca[i];
          while (c.charAt(0) == ' ') c = c.substring(1, c.length);
          if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
        }

        return null;
      }

      localizeUrl($url) {
        const rootWithoutEndingSlash = Shopify.routes.root.slice(0, -1);
        const urlParts = $url.split('/');
        urlParts[0] = rootWithoutEndingSlash;
        return urlParts.join('/');
      }

      storageAvailable(type) {
        let storage;
        try {
          storage = window[type];
          const x = '__storage_test__';
          storage.setItem(x, x);
          storage.removeItem(x);
          return true;
        } catch (e) {
          return (
            e instanceof DOMException &&
            // everything except Firefox
            (e.code === 22 ||
              // Firefox
              e.code === 1014 ||
              // test name field too, because code might not be present
              // everything except Firefox
              e.name === 'QuotaExceededError' ||
              // Firefox
              e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
            // acknowledge QuotaExceededError only if there's something already stored
            storage &&
            storage.length !== 0
          );
        }
      }

      /** {latitude, longitude} as numbers, or null when the position has no usable coordinates. */
      getValidCoords(position) {
        const latitude = parseFloat(position?.coords?.latitude);
        const longitude = parseFloat(position?.coords?.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
        return { latitude, longitude };
      }

      /** Runs onGranted only when the Permissions API says geolocation is already 'granted'. */
      whenGeolocationGranted(onGranted, onOtherwise) {
        if (!navigator.geolocation || !navigator.permissions?.query) {
          onOtherwise();
          return;
        }
        navigator.permissions.query({ name: 'geolocation' })
          .then((status) => (status.state === 'granted' ? onGranted() : onOtherwise()))
          .catch(() => onOtherwise());
      }

      getDistanceFromKm(km) {
        let distanceText = '';
        if (this.dataset.distanceUnit === 'km') {
          const parsedKm = parseFloat(km.toFixed(1));
          distanceText = `${parsedKm} ${this.dataset.distanceKmLabel}`
        } else {
          const miles = km * 0.621371;
          const parsedMiles = parseFloat(miles.toFixed(1));
          distanceText = `${parsedMiles} ${this.dataset.distanceMilesLabel}`
        }
        return distanceText;
      }

      init() {
        function accept(pos, cookie = false) {
          if (cookie && pos) {
            const position = {
              coords: {
                latitude: pos?.coords?.latitude,
                longitude: pos?.coords?.longitude,
              }
            };

            this.setSecureCookie('location_sharing', 'true', this.expiry);
            this.setSecureCookie('location_position', JSON.stringify(position), this.expiry);
          }
          this.fetchLocations(pos);
        }

        function decline(cookie = false) {
          if (cookie) this.setSecureCookie('location_sharing', 'false', this.expiry);

          this.fetchLocations(null, true, () => {
            const hasUpdated = this.updateMyStoreFromPreferredStoreId();

            if (!hasUpdated) this.renderSelectYourStore();
          });
        }

        function getLocation() {
          const enableCookie = SDG.Data.locationCookie || false;

          if (enableCookie) {
            const locationPreference = this.getCookie('location_sharing') || null;

            if (locationPreference && locationPreference === 'false') {
              decline.call(this, false);

              this.hasInitialized = true;
              return;
            }

            if (locationPreference && locationPreference === 'true') {
              const locationPosition = this.getCookie('location_position') || null;

              if (locationPosition) {
                let pos = null;
                try {
                  pos = JSON.parse(locationPosition);
                } catch (_) {}
                // WD - check if the position has valid coordinates
                // pos.coords = {
                //   latitude: 32.171775,
                //   longitude: 34.922838
                // }
                if (this.getValidCoords(pos)) {

                  accept.call(this, pos, true);
                  this.hasInitialized = true;
                  return;
                }else{
                  // Older code could store "{}" (a stringified GeolocationPosition). That is our
                  // bug, not the user's: drop it silently, keep location_sharing, and re-read the
                  // position only if the browser already granted it (no prompt).
                  this.setSecureCookie('location_position', '', -1);
                  this.hasInitialized = true;
                  this.whenGeolocationGranted(
                    () => navigator.geolocation.getCurrentPosition(
                      (freshPos) => accept.call(this, freshPos, true),
                      () => decline.call(this, false)
                    ),
                    () => decline.call(this, false)
                  );
                  return;
                }
                //WD - end
              }
            }

            navigator.geolocation.getCurrentPosition((pos) => accept.call(this, pos, true), decline.bind(this));
            this.hasInitialized = true;

            return;
          }

          navigator.geolocation.getCurrentPosition(accept.bind(this), decline.call(this, false));
          this.hasInitialized = true;
        }

        if (this.dataset.defaultLocationId !== '') {
          this.preferredStoreId = `gid://shopify/Location/${this.dataset.defaultLocationId}`;
        }

        if (this.storageAvailable('localStorage')) {
          const preferredStoreId = localStorage.getItem('preferredStoreId');
          if (preferredStoreId) {
            this.preferredStoreId = preferredStoreId;
          }
        }

        const exploreEilatOnMap =
          SDG?.Data?.mapPage &&
          new URLSearchParams(window.location.search).get('exploreEilat') === 'true';
        if (exploreEilatOnMap) {
          // Load stores once; the drawer applies Eilat search + 5km after maps are ready.
          this.fetchLocations(null, true);
          this.hasInitialized = true;
          return;
        }

        if (!navigator.geolocation) {
          this.fetchLocations(null, true, () => {
            const hasUpdated = this.updateMyStoreFromPreferredStoreId();
            if (!hasUpdated) this.renderSelectYourStore();
          });
        } else {
          getLocation.call(this);
        }
      }

      // update cart attributes — returns a Promise so callers can serialize with cart/add.js
      updateSelectedLocationAttributes(storeLocation = null) {
        const nextLocation = storeLocation || '';
        const currentLocation = this.dataset.cartSelectedLocation || '';

        if (currentLocation === nextLocation) {
          return Promise.resolve();
        }

        const data = {
          updates: {},
          attributes: {
            _selected_location: storeLocation,
          },
        };

        let updateAwait = Promise.resolve();
        if (window.isUpdatingCart) {
          updateAwait = new Promise((resolve) => {
            document.addEventListener('wd:cart:updated', resolve, { once: true });
            setTimeout(() => resolve(), 15000); // fallback so it never hangs

          });
        }

        return updateAwait.then(() => new Promise((resolve) => {
          $.ajax({
            type: 'POST',
            url: `${window.Shopify.routes.root}cart/update.js`,
            data: data,
            dataType: 'json',
            success: (response) => {
              this.dataset.cartSelectedLocation = nextLocation;
              resolve(response);
            },
            error: function (e) {
              console.log(e);
              resolve();
            },
          });
        }));
      }

      updateFacets(placeholderText = null) {
        if(!this?.filterResults) return;
        const $placeholder = document.querySelector(`${this.dom.filterInput}[data-location-id="placeholder"]`);
        const $placeholderButton = $placeholder?.closest('li').querySelector(this.dom.searchButton);
        if (placeholderText && $placeholderButton) {
          $placeholderButton.innerText = placeholderText;
        }

        const $prevInput = document.querySelector(`${this.dom.filterInput}.active`);
        const $prevInputChecked = $prevInput?.checked;
        let $visibleInput = null;

        this.allLocationsData.forEach((location) => {
          const $filterTags = document.querySelectorAll(`${this.dom.filterTag}[data-location-id="${location.id}"]`);
          $filterTags.forEach(($filterTag) => {
            $filterTag.innerText = location.name;
          });

          const $filterInputs = document.querySelectorAll(this.dom.filterInput);
          $filterInputs.forEach(($filterInput) => {
            const pickupLocationId = $filterInput.dataset?.locationId;

            // update pickup names
            if (pickupLocationId === location.id) {
              $filterInput.setAttribute('aria-label', location.name);
              const $searchButton = $filterInput.closest('li').querySelector(this.dom.searchButton);
              if ($searchButton) {
                $searchButton.innerText = location.name;
              }
            }
            // set preferred store as visible
            if (pickupLocationId === this.preferredStoreId) {
              $visibleInput = $filterInput;
              $filterInput.disabled = false;
              $filterInput.classList.add('active');
              $filterInput.closest('label').classList.remove('facet-checkbox--disabled');
              // wedev change: update aria
              const $activeLi = $filterInput.closest('li');
              $activeLi.classList.add('active');
              $activeLi.removeAttribute('aria-disabled');
              $activeLi.removeAttribute('tabindex');
            } else if (pickupLocationId !== 'placeholder') {
              $filterInput.disabled = true;
              $filterInput.checked = false;
              $filterInput.classList.remove('active');
              $filterInput.closest('label').classList.add('facet-checkbox--disabled');
              // wedev change: update aria
              const $disabledLi = $filterInput.closest('li');
              $disabledLi.classList.remove('active');
              $disabledLi.setAttribute('aria-disabled', 'true');
              $disabledLi.setAttribute('tabindex', '0');
            }
          });
        });

        if ($visibleInput && $prevInput && $visibleInput?.id !== $prevInput?.id && $prevInputChecked) {
          // simulate click to update the active state
          // if the previous preferred store was checked
          const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
          });
          $visibleInput.dispatchEvent(clickEvt);
        }

        // show placeholder if preferred store is not an available filter
        if (!$visibleInput && $placeholder) {
          $placeholder.closest('li').classList.add('active');

          //clear active location filter
          const $filterTag = document.querySelector(`${this.dom.filterTag}[data-location-id]`);
          if ($filterTag) {
            const clickEvt = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
            });
            $filterTag.closest('a').dispatchEvent(clickEvt);
          }
        } else if ($placeholder) {
          $placeholder.closest('li').classList.remove('active');
        }
      }

      updateMyStoreFromPreferredStoreId() {
        let hasUpdated = false;
        let hasLocalStoragePreferredStoreId = false;

        if (this.storageAvailable('localStorage')) {
          const preferredStoreId = localStorage.getItem('preferredStoreId');
          if (preferredStoreId) {
            hasLocalStoragePreferredStoreId = true;
          }
        }

        // set store only if there is a preferred store and no geolocation or it came from localStorage
        if (this.preferredStoreId && (!this.myCoords || hasLocalStoragePreferredStoreId)) {
          const preferredLocation = this.allLocationsData.find(
            (location) => location.id === this.preferredStoreId
          );
          if (preferredLocation) {
            this.updateMyStoreWithLocation(preferredLocation);
            hasUpdated = true;
          }
        }
        return hasUpdated;
      }

      locationsQuery(productQueryIds = []) {
        const activeLang = SDG?.Data?.language || 'EN';

        const locationQuery = `
          id
          name
          address {
            formatted
            address1
            city
            province
            provinceCode
            zip
            country
            phone
            latitude
            longitude
          }
          enableToggles: metafield(namespace: "apple", key: "apl_EnableToggles") {
            value
          }
          pickupMessages: metafield(namespace: "apple", key: "apl_PickupMessages") {
            value
          }
          phoneOverride: metafield(namespace: "apple", key: "apl_PhoneOverride") {
            value
          }
          email: metafield(namespace: "apple", key: "apl_Email") {
            value
          }
          hours: metafield(namespace: "apple", key: "apl_Hours") {
            value
          }
          services: metafield(namespace: "apple", key: "apl_ServicesContent") {
            value
          }
          waze: metafield(namespace: "custom", key: "waze_link") {
            value
          }
          googleMaps: metafield(namespace: "custom", key: "google_maps_link") {
            value
          }
          isOpenSaturday: metafield(namespace: "custom", key: "is_open_saturday") {
            value
          }
          isOpenLate: metafield(namespace: "custom", key: "open_late") {
            value
          }
          allCustomers: metafield(namespace: "custom", key: "all_costumers") {
            value
          }
          idigital: metafield(namespace: "custom", key: "idigital") {
            value
          }
          isLabService: metafield(namespace: "custom", key: "is_lab_service") {
            value
          }
          isTechnicalSupport: metafield(namespace: "custom", key: "is_technichal_support") {
            value
          }
          image: metafield(namespace: "apple", key: "apl_StoreImage") {
            reference {
              ... on MediaImage {
                image {
                  originalSrc
                }
              }
            }
          }
        `;
        const getNearestQuery = `
          getNearestPickupLocations($first: Int!, $location: GeoCoordinateInput, $sortKey: LocationSortKeys) @inContext(language: ${activeLang}){
            locations(near: $location, first: $first, sortKey: $sortKey) {
              edges {
                node {
                  ${locationQuery}
                }
              }
            }
          }
        `;
        if (productQueryIds.length > 0) {
          let productsQuery = '';
          let productIds = '';
          let selectedOptions = '';

          productQueryIds.forEach((key) => {
            productIds += `$productId${key}: ID!,`;
            selectedOptions += `$selectedOptions${key}: [SelectedOptionInput!]!,`;
            productsQuery += `
              product${key}: product(id: $productId${key}) {
                variantBySelectedOptions(selectedOptions: $selectedOptions${key}) {
                  marlogStock: metafield(namespace: "custom", key: "marlog_stock") {
                    value
                  }
                  storeAvailability(first: $first) {
                    edges {
                      node {
                        available
                        pickUpTime
                        quantityAvailable
                        location {
                          ${locationQuery}
                        }
                      }
                    }
                  }
                }
              }
            `;
          });
          return `
            query Products(${productIds} ${selectedOptions} $first: Int!, $location: GeoCoordinateInput, $sortKey: LocationSortKeys) @inContext(language: ${activeLang}){
              ${productsQuery}
              locations(near: $location, first: $first, sortKey: $sortKey) {
                edges {
                  node {
                    ${locationQuery}
                  }
                }
              }
            }
          `;
        }
        return `
          query ${getNearestQuery}
        `;
      }

      async fetchLocations(position = null, setDataOnly = false, cb = null) {
        const queryVariables = {
          first: parseInt(this.dataset.numberOfLocationsToFetch),
          sortKey: 'NAME',
        };
        const validCoords = this.getValidCoords(position);
        if (validCoords) {
          this.myCoords = validCoords;
          const { latitude, longitude } = validCoords;
          queryVariables.sortKey = 'DISTANCE';
          queryVariables.location = {
            latitude,
            longitude,
          };
        }
        let isCart = false;
        const pickupAvailabilities = wdPickupAvailabilitiesOutsideCartDrawer(this.dom.pickupAvailability);
        const productQueryIds = [];
        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.every((pickupAvailability, i) => {
            const itemKey = pickupAvailability?.dataset?.itemKey;
            const productId = pickupAvailability?.dataset?.productId;
            const selectedOptionsRaw = pickupAvailability?.dataset?.selectedOptions;
            if (productId && selectedOptionsRaw) {
              const selectedOptions = JSON.parse(selectedOptionsRaw).map((option) => {
                return {
                  name: option.name,
                  value: option.selected_value ? option.selected_value : option.values[0],
                };
              });
              // if it is a cart item, use the itemKey as the key
              if (itemKey) {
                productQueryIds.push(itemKey);
                queryVariables[`productId${itemKey}`] = `gid://shopify/Product/${productId}`;
                queryVariables[`selectedOptions${itemKey}`] = selectedOptions;
                isCart = true;
              } else {
                // else use the index as the key
                productQueryIds.push(i);
                queryVariables[`productId${i}`] = `gid://shopify/Product/${productId}`;
                queryVariables[`selectedOptions${i}`] = selectedOptions;
              }
              // break out of the loop if there is no itemKey which means
              // we are on the PDP and should have only 1 product to fetch
              return itemKey;
            }
          });
        }
        const query = this.locationsQuery(productQueryIds);
        const locationsData = await this.fetchStorefrontApi(query, queryVariables);
        if (locationsData) {
          // get locations sorted by distance
          if (locationsData.locations?.edges?.length) {
            locationsData.locations.edges.forEach((location) => {
              // parse the services rich text
              if (location?.node?.services?.value) {
                const parsed = JSON.parse(location.node.services.value);
                const markup = this.serializeRichText(parsed);

                // add the markup to the location object
                location.node.services_html = markup;
              }

              if (location?.node?.image?.reference?.image?.originalSrc) {
                location.node.image_url = location.node.image.reference.image.originalSrc;
              }
            });

            // set all locations data
            this.allLocationsData = locationsData.locations.edges.map((location) => {
              const locationData = location.node;
              // set the available flag to false if we are on the PDP or cart page
              // to be overwriten later if the location is available
              if (locationsData.hasOwnProperty('product0')) {
                locationData.available = false;
                locationData.productPickupOfferedAtLocation = false;
              }
              locationData.distance = this.calculateStoreDistance(locationData.address);
              return locationData;
            }).filter((location) => {
              // filter out locations that are not enabled on the store locator page
              return JSON.parse(location.enableToggles?.value || null)?.storeLocatorEnabled !== false;
            });
          }

          // check if there is 1 PDP product
          if (locationsData.product0?.variantBySelectedOptions?.storeAvailability?.edges?.length) {
            const locationsPDPData = locationsData.product0.variantBySelectedOptions.storeAvailability.edges;
            const marlogStock = locationsData.product0.variantBySelectedOptions.marlogStock?.value || null;
            // update location data to incorporate the product data
            this.allLocationsData = this.allLocationsData.map((location) => {
              const locationPDPData = locationsPDPData.find((locationPDP) => locationPDP.node.location.id === location.id);
              if (locationPDPData) {
                const locationData = {
                  ...location,
                  available: locationPDPData.node.available,
                  pickUpTime: locationPDPData.node.pickUpTime,
                  quantityAvailable: locationPDPData.node.quantityAvailable,
                  productPickupOfferedAtLocation: true,
                  marlogStock:marlogStock,
                };
                return locationData;
              }
              return location;
            });
          // check if there are cart products
          } else if (isCart) {
            // reset the itemLocationsData object
            this.itemLocationsData = {};
            const locationKeys = Object.keys(locationsData);
            locationKeys.forEach((key) => {
              if (key.includes('product')) {
                const itemKey = key.replace('product', '');
                const lineItem = locationsData[key];
                const locationsItemData = lineItem?.variantBySelectedOptions?.storeAvailability?.edges || [];
                // set the cart item specfic locations
                const lineItemLocations = this.allLocationsData.map((location) => {
                  const locationItemData = locationsItemData.find((locationItem) => locationItem.node.location.id === location.id);
                  if (locationItemData) {
                    const locationData = {
                      ...location,
                      available: locationItemData.node.available,
                      pickUpTime: locationItemData.node.pickUpTime,
                      quantityAvailable: locationItemData.node.quantityAvailable,
                      productPickupOfferedAtLocation: true,
                      marlogStock: lineItem.variantBySelectedOptions.marlogStock?.value || null,
                    };
                    return locationData;
                  }
                  const locationData = {
                    ...location,
                    available: false,
                  };
                  return locationData;
                });
                // apply marlog adjustment: quantityAvailable includes warehouse stock, subtract marlog to get location-specific stock
                lineItemLocations.forEach((location) => {
                  const isEilat = location.name.includes("אילת");
                  if (location.marlogStock && typeof location.quantityAvailable === 'number' && !isEilat) {
                    location.quantityAvailable = Math.max(0, location.quantityAvailable - Number(location.marlogStock));
                  }
                  if (location.quantityAvailable === 0) {
                    location.available = false;
                  }
                });
                // add the rest to another object to be used on the cart page
                this.itemLocationsData[itemKey] = lineItemLocations;
              }
            });
          }
          this.storeSuggestions = [];
          this.allLocationsData.forEach((location) => {
            this.storeSuggestions.push(
              location.address.city,
              location.address.province,
              location.address.zip,
              location.address.country,
            );
          });
          // Apply marlog adjustment before updateMyStore so renderPreview receives correct location-specific stock
          this.allLocationsData.forEach((location) => {     
            const isEilat = location.name.includes("אילת");
            if (location.marlogStock && !isEilat) {
              location.quantityAvailable = location.quantityAvailable - Number(location.marlogStock);
            }
            if (location.quantityAvailable === 0) {
              location.available = false;
            }
          });
          if (!setDataOnly) {
            const hasUpdated = this.updateMyStoreFromPreferredStoreId();
            if (!hasUpdated) {
              const firstLocation = this.allLocationsData.find((location) => JSON.parse(location.enableToggles?.value || null)?.checkoutEnabled !== false);
              this.updateMyStoreWithLocation(firstLocation);
            }
            if (typeof wdStoreLocatorDrawerIsEilatMode === 'function') {
              this.wdReassignPreferredStoreForEilatMode(wdStoreLocatorDrawerIsEilatMode());
            }
          }
          if (this.drawer) {
            let allLocationsData = this.allLocationsData;
            if (this.drawer.selectedItemLocationsKey) {
              const selectedItemLocationsData = this.itemLocationsData[this.drawer.selectedItemLocationsKey];
              if (selectedItemLocationsData) {
                allLocationsData = selectedItemLocationsData;
              }
            }

            let searchLocation = this.storeLocatorSearch.value;
            searchLocation = searchLocation.trim();
            // WD - an active query goes through the search so the list stays filtered/ordered
            if (searchLocation) {
              this.drawer.searchLocations(true);
            } else {
              this.drawer.updateStoreDetails(allLocationsData, searchLocation);
            }
          }
        }
        if (cb) {
          cb();
        }
      }

      serializeRichText(richText) {
        let html = '';
        switch (richText.type) {
          case 'root':
            richText.children.forEach(child => {
              html += this.serializeRichText(child);
            });
            break;
          case 'list':
            html += `<ul>`;
            richText.children.forEach(child => {
              html += this.serializeRichText(child);
            });
            html += `</ul>`;
            break;
          case 'list-item':
            html += `<li>`;
            richText.children.forEach(child => {
              html += this.serializeRichText(child);
            });
            html += `</li>`;
            break;
          case 'text':
            html += richText.value;
            break;
          case 'link':
            html += `<a href="${richText.url}">`;
            richText.children.forEach(child => {
              html += this.serializeRichText(child);
            });
            html += `</a>`;
            break;
          case 'paragraph':
            html += `<p>`;
            richText.children.forEach(child => {
              html += this.serializeRichText(child);
            });
            html += `</p>`;
            break;
          default:
            break;
        }
        return html;
      }

      onClickRefreshList(evt) {
        evt.currentTarget.disabled = true;
        this.init();
      }

      onClickToggleDetails() {
        if (this.details.hasAttribute('open')) {
          this.hideDetails();
        } else {
          this.showDetails();
        }
      }

      onClickShowStoreSelector(evt) {
        document.querySelector('my-store-locator-drawer').show(evt.currentTarget);
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this.details &&
          !target.closest(this.dom.btn) &&
          !target.closest(this.dom.details)
        ) {
          this.hideDetails();
        }
      }

      hideDetails() {
        this.details.removeAttribute('open');
        if (this.button) this.button.setAttribute('aria-expanded', 'false');
        document.body.removeEventListener('click', this.onBodyClick);
        removeTrapFocus(this.focusElement);
      }

      showDetails(focusElement) {
        this.focusElement = focusElement;
        this.details.setAttribute('open', '');
        if (this.button) this.button.setAttribute('aria-expanded', 'true');
        document.body.addEventListener('click', this.onBodyClick);
        trapFocus(this.details);
      }

      removeButtonEvents() {
        this.button.removeEventListener('click', this.onClickRefreshList);
        this.button.removeEventListener('click', this.onClickToggleDetails);
        this.button.removeEventListener('click', this.onClickShowStoreSelector);
      }

      renderError() {
        this.removeButtonEvents();
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = this.dataset.errorText;
        this.button.addEventListener('click', this.onClickRefreshList);

        if (this.mobileLocator) {
          this.mobileLocator.renderError();
        }

        const pickupAvailabilities = wdPickupAvailabilitiesOutsideCartDrawer(this.dom.pickupAvailability);
        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.forEach((pickupAvailability) => {
            pickupAvailability.renderError();
          });
        }

        this.updateFacets(this.dataset.errorText);
      }

      renderSelectYourStore() {
        this.removeButtonEvents();
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = this.dataset.selectText;
        this.button.addEventListener('click', this.onClickShowStoreSelector);

        this.drawer.loading.classList.add('hidden');

        if (this.mobileLocator) {
          this.mobileLocator.renderSelectYourStore();
        }

        const pickupAvailabilities = wdPickupAvailabilitiesOutsideCartDrawer(this.dom.pickupAvailability);
        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.forEach((pickupAvailability) => {
            pickupAvailability.renderPreview();
          });
        }

        this.updateFacets(this.dataset.selectText);
      }

      updateMyStoreWithLocation(location, opts = {}) {
        if (!location) return;
        this.removeButtonEvents();
        const {
          name,
          address,
          email,
          hours,
          phoneOverride,
          id,
          distance,
        } = location;
        this.preferredStoreId = id;
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = name;
        this.button.addEventListener('click', this.onClickToggleDetails);

        function resetValue(element, attribute = false) {
          if (!element) return;

          element.value = '';
          if (attribute) {
            element.setAttribute(attribute, '');
          }
        }

        this.updateFacets(name);
        if (opts.skipCartAttributes) {
          this.dataset.cartSelectedLocation = name;
        }
        const locationAttrSync = opts.skipCartAttributes
          ? Promise.resolve()
          : this.updateSelectedLocationAttributes(name);

        this.details.querySelector('h2').innerHTML = name;
        this.detailsLocation.innerHTML = address?.city && address?.provinceCode ? (`${address.city}, ${address.provinceCode}`) : (`${address.city}`);
        if (address?.phone && address?.phone !== '') {
          this.detailsTelephone.classList.remove('hidden');
          const phoneLink = this.detailsTelephone.querySelector('a');
          phoneLink.href = `tel:${address.phone}`;
          phoneLink.innerHTML = address.phone;
        } else {
          const phoneLink = this.detailsTelephone.querySelector('a');
          resetValue(phoneLink, 'href');
          this.detailsTelephone.classList.add('hidden');
        }
        if (phoneOverride?.value && phoneOverride?.value !== '') {
          this.detailsTelephone.classList.remove('hidden');
          const phoneLink = this.detailsTelephone.querySelector('a');
          phoneLink.href = `tel:${phoneOverride.value}`;
          phoneLink.innerHTML = phoneOverride.value;
        }
        if (address?.address1 && address?.address1 !== '') {
          this.detailsAddress.classList.remove('hidden');
          const apiProvider = SDG?.Data?.mapProvider  || null;
          const addressLink = this.detailsAddress.querySelector('a');
          const encodedName = encodeURIComponent(name);
          const latitude = address.latitude;
          const longitude = address.longitude;

          if (apiProvider === 'google_maps') {
            const href = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${latitude},${longitude}`;
            addressLink.href = href;
            addressLink.innerHTML = address.address1;
          }

          if (apiProvider === 'apple_maps') {
            const encodedAddress = encodeURIComponent(address.formatted);
            const href = `https://maps.apple.com/place?q=${encodedName}&address=${encodedAddress}&ll=${latitude},${longitude}`;
            addressLink.href = href;
            addressLink.innerHTML = address.address1;
          }
        } else {
          const addressLink = this.detailsAddress.querySelector('a');
          resetValue(addressLink, 'href');
          this.detailsAddress.classList.add('hidden');
        }
        if (email?.value && email?.value !== '') {
          this.detailsEmail.classList.remove('hidden');
          const emailLink = this.detailsEmail.querySelector('a');
          emailLink.href = `mailto:${email.value}`;
          emailLink.innerHTML = email.value;
        } else {
          const emailLink = this.detailsEmail.querySelector('a');
          resetValue(emailLink, 'href');
          this.detailsEmail.classList.add('hidden');
        }
        if (hours?.value && hours?.value !== '') {
          this.detailsHours.classList.remove('hidden');
          const hoursSpan = this.detailsHours.querySelector('span');
          hoursSpan.innerHTML = hours.value;
        } else {
          const hoursSpan = this.detailsHours.querySelector('span');
          resetValue(hoursSpan);
          this.detailsHours.classList.add('hidden');
        }
        if (location?.services_html) {
          const parser = new DOMParser();
          const servicesHtml = parser.parseFromString(location.services_html, "text/html");
          const servicesLinks = servicesHtml.querySelectorAll('a');
          let localizedServices;

          // Update url with locale
          if (servicesLinks && servicesLinks.length > 0) {
            Array.from(servicesLinks).forEach((link) => {
              const href = link.getAttribute('href');
              const slashIndex = href.indexOf('/');

              if (slashIndex === 0) {
                const localizedUrl = this.localizeUrl(href);
                link.setAttribute('href', localizedUrl);
                link.classList.add('apl-section-stores-locator-store-services-link');
              }
            });

            localizedServices = servicesHtml.body.innerHTML;
          }else{
            localizedServices = location.services_html;
          }

          this.detailsServices.classList.remove('hidden');
          this.detailsServices.innerHTML = `
            <button class="my-location-result__services-btn js-acc-button apl-section-stores-locator-store-services-btn" type="button">
              <span class="underlined-text">${SDG?.Data?.storeServiceTrigger ? SDG.Data.storeServiceTrigger : 'View store services'}</span>
              <i class="js-acc-icon myarrow fa fa-chevron-down my-location-result__services-icon" aria-hidden="true"></i>
            </button>
            <div class="my-location-result__services js-acc-details" aria-expanded="false">${localizedServices}</div>
          `;
          //WD - add location map links to my location
          if(this.querySelector('.wd-location-map-links')){
            this.querySelector('.wd-location-map-links').innerHTML = `
            ${location?.waze?.value || location?.googleMaps?.value ? (`
                <div class="wd-location-map-links">
                  ${location.waze?.value ?(
              `<a class="wd-location-waze" href="${location.waze.value}" aria-label="נווט עם וויז ל${location.name || ''}"> 
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                        <g clip-path="url(#clip0_428_7687)">
                          <path d="M25 10.9375C25 5.3375 20.0922 0.78125 14.0625 0.78125C8.03125 0.78125 3.125 5.3375 3.13438 11.0578C3.1375 11.0781 3.42344 13.1016 2.51094 14.1687C2.12188 14.6219 1.55625 14.8438 0.78125 14.8438C0.35 14.8438 0 15.1938 0 15.625C0 17.7906 1.60469 19.2953 4.84219 20.1719C4.75156 20.4656 4.6875 20.7703 4.6875 21.0938C4.6875 22.8172 6.08906 24.2188 7.8125 24.2188C9.53594 24.2188 10.9375 22.8172 10.9375 21.0938C10.9375 21.0672 10.9297 21.0438 10.9297 21.0172C11.9094 21.0672 12.9484 21.0938 14.0625 21.0938C14.0625 22.8172 15.4641 24.2188 17.1875 24.2188C18.9109 24.2188 20.3125 22.8172 20.3125 21.0938C20.3125 20.5203 20.1453 19.9891 19.875 19.525C22.9484 17.725 25 14.5531 25 10.9375ZM18.7172 18.3859C18.2641 18.1281 17.7469 17.9688 17.1875 17.9688C16.0453 17.9688 15.0531 18.5922 14.5094 19.5109C14.3594 19.5172 14.2125 19.5312 14.0625 19.5312C12.7406 19.5312 11.5563 19.4828 10.4391 19.4141C9.88281 18.5469 8.91562 17.9688 7.8125 17.9688C7.00625 17.9688 6.27656 18.2844 5.72187 18.7875C3.45156 18.25 2.07187 17.4281 1.68125 16.325C2.50625 16.1687 3.18125 15.7875 3.69844 15.1844C5.08594 13.5641 4.69688 10.9375 4.6875 10.9375C4.6875 6.19844 8.89219 2.34375 14.0625 2.34375C19.2328 2.34375 23.4375 6.19844 23.4375 10.9375C23.4375 14.1219 21.5328 16.9016 18.7172 18.3859Z" fill="black"/>
                          <path d="M10.9688 7.8125H10.9531C10.5219 7.8125 10.1797 8.1625 10.1797 8.59375C10.1797 9.025 10.5375 9.375 10.9688 9.375C11.4 9.375 11.75 9.025 11.75 8.59375C11.75 8.1625 11.4 7.8125 10.9688 7.8125Z" fill="black"/>
                          <path d="M17.2188 7.8125H17.2031C16.7719 7.8125 16.4297 8.1625 16.4297 8.59375C16.4297 9.025 16.7875 9.375 17.2188 9.375C17.65 9.375 18 9.025 18 8.59375C18 8.1625 17.65 7.8125 17.2188 7.8125Z" fill="black"/>
                          <path d="M18.1392 11.7377C17.7158 11.6439 17.3017 11.908 17.2048 12.3283C16.8736 13.8095 15.5814 14.8439 14.0627 14.8439C12.5455 14.8439 11.2517 13.8095 10.9189 12.3283C10.8236 11.9064 10.4064 11.647 9.98453 11.7377C9.56421 11.833 9.30015 12.2502 9.3939 12.672C9.88922 14.872 11.8095 16.4064 14.0627 16.4064C16.3173 16.4064 18.2377 14.8705 18.7314 12.672C18.8267 12.2502 18.5611 11.833 18.1392 11.7377Z" fill="black"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_428_7687">
                            <rect width="25" height="25" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </a>`
                  ): ''}
                  ${location.googleMaps?.value ?(
              `<a class="wd-location-google_maps" href="${location.googleMaps.value}" aria-label="נווט עם גוגל מפות ל${location.name || ''}"> 
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                        <path d="M20.896 5.19578C20.1368 3.6743 18.9943 2.38979 17.5917 1.481C16.0963 0.512107 14.336 0 12.501 0C10.6659 0 8.90559 0.512107 7.41023 1.481C6.00765 2.38974 4.86508 3.6743 4.10595 5.19578C3.34599 6.71892 3.00659 8.40675 3.12441 10.0767C3.2501 11.8571 3.90073 13.5743 5.00605 15.0429L12.5009 25L19.9958 15.0429C21.1011 13.5744 21.7518 11.8571 21.8774 10.0767C21.9953 8.40675 21.656 6.71897 20.896 5.19578ZM19.2023 5.16516L16.681 8.51466C16.2725 6.58196 14.5536 5.12693 12.501 5.12693C12.3978 5.12693 12.2956 5.13084 12.1942 5.13807L14.7294 1.77006C16.5812 2.29496 18.1548 3.47826 19.2023 5.16516ZM12.501 6.59177C14.0491 6.59177 15.3086 7.85124 15.3086 9.39938C15.3086 10.9475 14.0491 12.207 12.501 12.207C10.9528 12.207 9.69338 10.9475 9.69338 9.39938C9.69338 7.85124 10.9528 6.59177 12.501 6.59177ZM12.501 1.46484C12.7056 1.46484 12.9082 1.4727 13.1089 1.48715L9.52776 6.2447L6.62415 4.05911C8.09339 2.40741 10.1769 1.46484 12.501 1.46484ZM5.41669 5.84978C5.52182 5.63909 5.63534 5.43577 5.75512 5.23836L8.64685 7.41501L5.0685 12.1689C4.29233 10.1421 4.39526 7.89699 5.41669 5.84978ZM5.80531 13.6258L8.32088 10.2839C8.72932 12.2167 10.4483 13.6718 12.501 13.6718C12.6041 13.6718 12.7063 13.6679 12.8076 13.6607L9.30334 18.3161L6.17645 14.162C6.04442 13.9866 5.92133 13.8075 5.80531 13.6258ZM18.8255 14.162L12.501 22.5643L10.2201 19.534L19.929 6.63562C20.8898 9.17936 20.4992 11.9383 18.8255 14.162Z" fill="black"/>
                      </svg>
                    </a>`
                  ): ''}
                </div>`) : ''}`
                // WD - end of location map links

          }
          const $accButton = this.detailsServices.querySelector('.js-acc-button');
          const $accDetails = this.detailsServices.querySelector('.js-acc-details');
          const $accIcon = this.detailsServices.querySelector('.js-acc-icon');
          const $servicesBtn = this.detailsServices.querySelector('button.apl-section-stores-locator-store-services-btn');
          if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'false');

          function openAcc(el) {
            el.setAttribute('aria-expanded', true)
            el.classList.add('is-active');
          }

          function closeAcc(el) {
            el.setAttribute('aria-expanded', false)
            el.classList.remove('is-active');
          }

          $accButton.addEventListener('click', () => {
            if ($accDetails.classList.contains('is-active')) {
              closeAcc($accDetails);
              if ($accIcon) closeAcc($accIcon);
              if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'false');
              wdAnnounceExpanded(false, $servicesBtn || $accButton);
            } else {
              openAcc($accDetails);
              if ($accIcon) openAcc($accIcon);
              if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'true');
              wdAnnounceExpanded(true, $servicesBtn || $accButton);
            }
          });
        } else {
          this.detailsServices.classList.add('hidden');
        }

        this.updateMyStoreDistance(distance);

        if (this.mobileLocator) {
          this.mobileLocator.updateMyStoreWithLocation(location);
        }

        const pickupAvailabilities = wdPickupAvailabilitiesOutsideCartDrawer(this.dom.pickupAvailability);
        if (pickupAvailabilities.length > 0) {
          pickupAvailabilities.forEach((pickupAvailability) => {
            pickupAvailability.renderPreview(location);
          });
        }

        return locationAttrSync;
      }

      updateMyStoreDistance(distance) {
        if (distance) {
          this.distance.innerHTML = distance;
          this.distance.classList.remove('hidden');
          if (this.mobileLocator?.distance) {
            this.mobileLocator.distance.innerHTML = distance;
            this.mobileLocator.distance.classList.remove('hidden');
          }
        } else {
          this.distance.classList.add('hidden');
          if (this.mobileLocator?.distance) {
            this.mobileLocator.distance.classList.add('hidden');
          }
        }
      }

      // https://stackoverflow.com/questions/27928/calculate-distance-between-two-latitude-longitude-points-haversine-formula
      getDistanceFromLatLon(lat1, lon1, lat2, lon2) {
        return this.getDistanceFromKm(this.getKmFromLatLon(lat1, lon1, lat2, lon2));
      }

      getKmFromLatLon(lat1, lon1, lat2, lon2) {
        var R = 6371; // Radius of the earth in km
        var dLat = this.deg2rad(lat2-lat1);
        var dLon = this.deg2rad(lon2-lon1);
        var a =
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon/2) * Math.sin(dLon/2)
          ;
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        var d = R * c; // Distance in km
        return d;
      }

      deg2rad(deg) {
        return deg * (Math.PI/180)
      }

      calculateStoreDistance(locationAddress) {
        if (
          this.myCoords?.latitude &&
          this.myCoords?.longitude &&
          locationAddress?.latitude &&
          locationAddress?.longitude
        ) {
          return this.getDistanceFromLatLon(
            this.myCoords.latitude,
            this.myCoords.longitude,
            locationAddress.latitude,
            locationAddress.longitude,
          );
        } else {
          return null;
        }
      }

      /**
       * Fetch data from Shopify Storefront API
       */
      async fetchStorefrontApi(query, variables = {}) {
        const endpoint = `https://${Shopify.shop}/api/2024-07/graphql.json`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.dataset.storefrontApiAccessToken,
          },
          body: JSON.stringify({ query, variables }),
        });

        const { data, errors } = await response.json();

        if (errors) {
          this.renderError();
          throw new Error(errors[0].message);
        }

        return data;
      }
    }
  );
}

if (!customElements.get('my-store-locator-mobile') ) {
  customElements.define(
    'my-store-locator-mobile',
    class MyStoreLocatorMobile extends HTMLElement {
      constructor() {
        super();

        this.dom = {
          desktop: 'my-store-locator',
          btn: '.js-my-store-locator-btn',
          drawerBtn: '.js-my-store-locator-drawer-btn',
          info: '.js-my-store-locator-info',
          details: '.js-my-store-locator-details',
          close: '.js-my-store-locator-close',
          location: '.js-my-store-locator-location',
          telephone: '.js-my-store-locator-telephone',
          email: '.js-my-store-locator-email',
          address: '.js-my-store-locator-address',
          hours: '.js-my-store-locator-hours',
          services: '.js-my-store-locator-services',
          distance: '.js-my-store-locator-distance',
        };

        this.desktopLocator = document.querySelector(this.dom.desktop);
        this.button = this.querySelector(this.dom.btn);
        this.drawerBtn = this.querySelector(this.dom.drawerBtn);
        if (this.drawerBtn) this.drawerBtn.addEventListener('click', this.onClickShowStoreSelector.bind(this));
        this.details = this.querySelector(this.dom.details);
        this.detailsClose = this.details.querySelector(this.dom.close);
        this.detailsClose.addEventListener('click', this.hideDetails.bind(this));
        this.detailsLocation = this.details.querySelector(this.dom.location);
        this.detailsTelephone = this.details.querySelector(this.dom.telephone);
        this.detailsEmail = this.details.querySelector(this.dom.email);
        this.detailsAddress = this.details.querySelector(this.dom.address);
        this.detailsHours = this.details.querySelector(this.dom.hours);
        this.detailsServices = this.details.querySelector(this.dom.services);
        this.distance = this.details.querySelector(this.dom.distance);


        this.onBodyClick = this.handleBodyClick.bind(this);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.onClickToggleDetails = this.onClickToggleDetails.bind(this);
        this.onClickShowStoreSelector = this.onClickShowStoreSelector.bind(this);

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hideDetails();
        });
      }

      localizeUrl($url) {
        const rootWithoutEndingSlash = Shopify.routes.root.slice(0, -1);
        const urlParts = $url.split('/');
        urlParts[0] = rootWithoutEndingSlash;
        return urlParts.join('/');
      }

      onClickRefreshList(evt) {
        evt.currentTarget.disabled = true;
        this.desktopLocator.init();
      }

      onClickToggleDetails() {
        if (this.details.hasAttribute('open')) {
          this.hideDetails();
        } else {
          this.showDetails();
        }
      }

      onClickShowStoreSelector(evt) {
        document.querySelector('my-store-locator-drawer').show(evt.currentTarget);
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this.details &&
          !target.closest(this.dom.btn) &&
          !target.closest(this.dom.details)
        ) {
          this.hideDetails();
        }
      }

      hideDetails() {
        this.details.removeAttribute('open');
        if (this.button) this.button.setAttribute('aria-expanded', 'false');
        document.body.removeEventListener('click', this.onBodyClick);
        removeTrapFocus(this.focusElement);
      }

      showDetails(focusElement) {
        // hide mobile menu if open
        const menuDrawer = document.getElementById('Details-menu-drawer-container');
        if (menuDrawer && menuDrawer?.hasAttribute('open')) {
          menuDrawer.querySelector('summary').click();
        }
        this.focusElement = focusElement;
        this.details.setAttribute('open', '');
        if (this.button) this.button.setAttribute('aria-expanded', 'true');
        document.body.addEventListener('click', this.onBodyClick);
        trapFocus(this.details);
      }

      removeButtonEvents() {
        this.button.removeEventListener('click', this.onClickRefreshList);
        this.button.removeEventListener('click', this.onClickToggleDetails);
        this.button.removeEventListener('click', this.onClickShowStoreSelector);
      }

      renderError() {
        this.removeButtonEvents();
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = this.dataset.errorText;
        this.button.addEventListener('click', this.onClickRefreshList);
      }

      renderSelectYourStore() {
        this.removeButtonEvents();
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = this.dataset.selectText;
        this.button.addEventListener('click', this.onClickShowStoreSelector);
      }

      updateMyStoreWithLocation(location) {
        this.removeButtonEvents();
        const { name, address, email, hours, phoneOverride } = location;
        this.button.disabled = false;
        this.button.querySelector(this.dom.info).innerHTML = name;
        this.button.addEventListener('click', this.onClickToggleDetails);

        function resetValue(element, attribute = false) {
          if (!element) return;

          element.value = '';
          if (attribute) {
            element.setAttribute(attribute, '');
          }
        }

        this.details.querySelector('h2').innerHTML = name;
        this.detailsLocation.innerHTML = address?.city && address?.provinceCode ? (`${address.city}, ${address.provinceCode}`) : (`${address.city}`);
        if (address?.phone && address?.phone !== '') {
          this.detailsTelephone.classList.remove('hidden');
          const phoneLink = this.detailsTelephone.querySelector('a');
          phoneLink.href = `tel:${address.phone}`;
          phoneLink.innerHTML = address.phone;
        } else {
          const phoneLink = this.detailsTelephone.querySelector('a');
          resetValue(phoneLink, 'href');
          this.detailsTelephone.classList.add('hidden');
        }
        if (phoneOverride?.value && phoneOverride?.value !== '') {
          this.detailsTelephone.classList.remove('hidden');
          const phoneLink = this.detailsTelephone.querySelector('a');
          phoneLink.href = `tel:${phoneOverride.value}`;
          phoneLink.innerHTML = phoneOverride.value;
        }
        if (address?.address1 && address?.address1 !== '') {
          this.detailsAddress.classList.remove('hidden');
          const apiProvider = SDG?.Data?.mapProvider  || null;
          const addressLink = this.detailsAddress.querySelector('a');
          const encodedName = encodeURIComponent(name);
          const latitude = address.latitude;
          const longitude = address.longitude;

          if (apiProvider === 'google_maps') {
            const href = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${latitude},${longitude}`;
            addressLink.href = href;
            addressLink.innerHTML = address.address1;
          }

          if (apiProvider === 'apple_maps') {
            const encodedAddress = encodeURIComponent(address.formatted);
            const href = `https://maps.apple.com/place?q=${encodedName}&address=${encodedAddress}&ll=${latitude},${longitude}`;
            addressLink.href = href;
            addressLink.innerHTML = address.address1;
          }
        } else {
          const addressLink = this.detailsAddress.querySelector('a');
          resetValue(addressLink, 'href');
          this.detailsAddress.classList.add('hidden');
        }
        if (email?.value && email?.value !== '') {
          this.detailsEmail.classList.remove('hidden');
          const emailLink = this.detailsEmail.querySelector('a');
          emailLink.href = `mailto:${email.value}`;
          emailLink.innerHTML = email.value;
        } else {
          const emailLink = this.detailsEmail.querySelector('a');
          resetValue(emailLink, 'href');
          this.detailsEmail.classList.add('hidden');
        }
        if (hours?.value && hours?.value !== '') {
          this.detailsHours.classList.remove('hidden');
          const hoursSpan = this.detailsHours.querySelector('span');
          hoursSpan.innerHTML = hours.value;
        } else {
          const hoursSpan = this.detailsHours.querySelector('span');
          resetValue(hoursSpan);
          this.detailsHours.classList.add('hidden');
        }

        if (location?.services_html) {
          const parser = new DOMParser();
          const servicesHtml = parser.parseFromString(location.services_html, "text/html");
          const servicesLinks = servicesHtml.querySelectorAll('a');
          let localizedServices;

          // Update url with locale
          if (servicesLinks && servicesLinks.length > 0) {
            Array.from(servicesLinks).forEach((link) => {
              const href = link.getAttribute('href');
              const slashIndex = href.indexOf('/');

              if (slashIndex === 0) {
                const localizedUrl = this.localizeUrl(href);
                link.setAttribute('href', localizedUrl);
                link.classList.add('apl-section-stores-locator-store-services-link');
              }
            });

            localizedServices = servicesHtml.body.innerHTML;
          }else{
            // WD - fix for apple theme
            localizedServices = location.services_html;
            //WD end
          }

          this.detailsServices.classList.remove('hidden');
          this.detailsServices.innerHTML = `
            <button class="my-location-result__services-btn js-acc-button apl-section-stores-locator-store-services-btn" type="button">
              <span class="underlined-text">${SDG?.Data?.storeServiceTrigger ? SDG.Data.storeServiceTrigger : 'View store services'}</span>
              <i class="js-acc-icon myarrow fa fa-chevron-down my-location-result__services-icon" aria-hidden="true"></i>
            </button>
            <div class="my-location-result__services js-acc-details" aria-expanded="false">${localizedServices}</div>
          `;
          const $accButton = this.detailsServices.querySelector('.js-acc-button');
          const $accDetails = this.detailsServices.querySelector('.js-acc-details');
          const $accIcon = this.detailsServices.querySelector('.js-acc-icon');
          const $servicesBtn = this.detailsServices.querySelector('button.apl-section-stores-locator-store-services-btn');
          if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'false');

          function openAcc(el) {
            el.setAttribute('aria-expanded', true)
            el.classList.add('is-active');
          }

          function closeAcc(el) {
            el.setAttribute('aria-expanded', false)
            el.classList.remove('is-active');
          }

          $accButton.addEventListener('click', () => {
            if ($accDetails.classList.contains('is-active')) {
              closeAcc($accDetails);
              if ($accIcon) closeAcc($accIcon);
              if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'false');
              wdAnnounceExpanded(false, $servicesBtn || $accButton);
            } else {
              openAcc($accDetails);
              if ($accIcon) openAcc($accIcon);
              if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'true');
              wdAnnounceExpanded(true, $servicesBtn || $accButton);
            }
          });
        } else {
          this.detailsServices.classList.add('hidden');
        }
      }
    }
  );
}

if (!customElements.get('my-store-locator-drawer') ) {
  customElements.define(
    'my-store-locator-drawer',
    class MyStoreLocatorDrawer extends HTMLElement {
      constructor() {
        super();

        this.dom = {
          storeLocatorSearch: 'storeLocatorSearch',
          form: 'storeLocatorSearchForm',
          storeLocator: 'my-store-locator',
          suggestions: '.js-store-locator-search-suggestions',
          error: '.js-store-locator-search-error',
          loading: 'storeLocatorDrawerLoading',
          results: 'myLocationResults',
          resultsCount: '.js-store-locator-results-count',
          activeResult: '.js-my-location-result[data-active="true"]',
          close: '.js-store-locator-drawer-close',
          useLocationBtn: '.js-store-locator-use-location-btn',
          exploreEilatBtn: '.js-store-locator-explore-eilat-btn',
          resultsDistance: '.js-store-locator-results-distance',
          selectBtn: '.js-store-locator-select-btn',
          productInfo: '.js-store-locator-product-info',
          acc_button: '.js-acc-button',
          acc_icon: '.js-acc-icon',
          acc_details: '.js-acc-details',
        };
        this.cls = {
          active: 'is-active',
        };

        this.form = document.getElementById(this.dom.form);
        this.results = document.getElementById(this.dom.results);
        this.resultsCount = this.querySelector(this.dom.resultsCount);
        this.resultsDistance = this.querySelector(this.dom.resultsDistance);
        this.storeLocatorSearch = document.getElementById(this.dom.storeLocatorSearch);
        this.storeLocator = document.querySelector(this.dom.storeLocator);
        this.suggestions = this.querySelector(this.dom.suggestions);
        this.error = this.querySelector(this.dom.error);
        this.loading = document.getElementById(this.dom.loading);
        this.close = this.querySelector(this.dom.close);
        this.useLocationBtn = this.querySelector(this.dom.useLocationBtn);
        this.exploreEilatBtn = this.querySelector(this.dom.exploreEilatBtn);
        this.selectBtn = this.querySelector(this.dom.selectBtn);
        this.selectBtnLabel = this.selectBtn?.textContent?.trim() || '';

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.close.addEventListener('click', () => {
          this.hide();
        });

        this.useLocationBtn.addEventListener('click', () => {
          this.useLocation();
        });

        if (this.exploreEilatBtn) {
          this.exploreEilatBtn.addEventListener('click', () => {
            this.exploreEilatStores(true);
          });
        }

        this.selectBtn.addEventListener('click', this.selectPreferredLocation.bind(this));

        this.setupSearchAutocomplete();

        // Every other key re-runs the search, which re-renders the list and drops focus,
        // so keys used to navigate the branch list have to opt out here.
        const keysThatSkipSearch = ['TAB', 'SPACE', 'ENTER', 'ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'HOME', 'END'];
        this.addEventListener('keyup', (event) => {
          if (keysThatSkipSearch.includes(event.code.toUpperCase())) {
            return;
          }
          if (event.code.toUpperCase() === 'ESCAPE') {
            // Close the suggestion list first; only then close the drawer.
            if (this.suggestions && !this.suggestions.classList.contains('hidden')) {
              this.setSuggestionsOpen(false);
              return;
            }
            this.hide();
            return;
          }
          this.searchLocations(true);

        });

        this.init();
      }

      get isOverlayDrawer() {
        return this.classList.contains('my-store-locator-drawer');
      }

      /** Combobox wiring for the city search suggestions list. */
      setupSearchAutocomplete() {
        if (!this.storeLocatorSearch || !this.suggestions) return;

        this.activeSuggestionIndex = -1;
        this.suggestions.id = 'storeLocatorSearchSuggestions';
        this.suggestions.setAttribute('role', 'listbox');
        this.suggestions.setAttribute('aria-label', 'הצעות מיקום');

        this.storeLocatorSearch.setAttribute('role', 'combobox');
        this.storeLocatorSearch.setAttribute('aria-autocomplete', 'list');
        this.storeLocatorSearch.setAttribute('aria-haspopup', 'listbox');
        this.storeLocatorSearch.setAttribute('aria-expanded', 'false');
        this.storeLocatorSearch.setAttribute('aria-controls', this.suggestions.id);
        this.storeLocatorSearch.removeAttribute('aria-owns');
        this.storeLocatorSearch.removeAttribute('aria-activedescendant');
        this.storeLocatorSearch.removeAttribute('aria-describedby');

        // Persistent live region (NVDA/JAWS). VoiceOver often ignores this while the caret is
        // in the text field, so on Apple the announcement goes through a role="alert" node.
        this.suggestionStatus = this.querySelector('.js-store-locator-suggestions-status')
          || document.getElementById('storeLocatorSuggestionsStatus');
        if (!this.suggestionStatus) {
          this.suggestionStatus = document.createElement('div');
          this.suggestionStatus.id = 'storeLocatorSuggestionsStatus';
          this.suggestionStatus.className = 'js-store-locator-suggestions-status visually-hidden';
          this.suggestions.insertAdjacentElement('afterend', this.suggestionStatus);
        }
        this.suggestionStatus.removeAttribute('role');
        this.suggestionStatus.setAttribute('aria-live', 'assertive');
        this.suggestionStatus.setAttribute('aria-atomic', 'true');
        this.suggestionStatus.textContent = '';

        this.storeLocatorSearch.addEventListener('keydown', (event) => {
          this.handleSuggestionKeydown(event);
        });

        this.suggestions.addEventListener('keydown', (event) => {
          this.handleSuggestionListKeydown(event);
        });

        this.suggestions.addEventListener('mousedown', (event) => {
          const option = event.target.closest('[role="option"]');
          if (!option) return;
          event.preventDefault();
          this.selectSuggestion(option);
        });
      }

      isAppleScreenReaderHost() {
        return /Mac|iPhone|iPad|iPod/.test(navigator.platform || '')
          || /Mac OS X|iPhone|iPad/.test(navigator.userAgent || '');
      }

      /**
       * Announce the full suggestion list after typing.
       * Mac/VoiceOver: a role="alert" node (live regions are unreliable in text fields).
       * Windows/NVDA: aria-live only.
       * Windows delay is longer so the "expanded" combobox announce can finish first.
       */
      announceSuggestionList(labels) {
        clearTimeout(this._suggestAnnounceTimer);
        if (!labels.length) return;

        const message = `${labels.length} הצעות: ${labels.join('. ')}`;
        const isApple = this.isAppleScreenReaderHost();
        // wedev change (a11y): this used to wait a second, which is longer than the 900ms the
        // input handler waits before running the search itself. On the first letter the two
        // announcements collided and the list was the one that got dropped, so it only seemed
        // to speak from the second letter on. Long enough for the combobox to finish saying
        // "expanded", short enough to land before the search.
        const delay = 600;

        this._suggestAnnounceTimer = setTimeout(() => {
          // wedev change (a11y): this used to read the list out loud through speechSynthesis on
          // every Apple device. An Apple device is not a screen reader, so every Mac visitor
          // heard the site talk. speakAlert puts the same sentence in a role="alert" node, which
          // a reader picks up and which is silent for everyone else.
          //
          // It is used on Windows too now. The persistent live region below was reliable only
          // from the second letter on: the first letter is also the moment the combobox reports
          // itself as expanded, and that announcement swallowed the one sitting in the region.
          // A freshly inserted alert node is a new object every time and survives it.
          this.speakAlert(message);
          if (this.suggestionStatus) this.suggestionStatus.textContent = '';
        }, delay);
      }

      /**
       * Body-level alert for VoiceOver when native listbox speech drops the city name.
       * Skipped on Windows so NVDA does not hear focus + alert as two voices.
       */
      speakAlert(message) {
        if (!message) return;

        if (this._alertNode) {
          this._alertNode.remove();
          this._alertNode = null;
        }
        clearTimeout(this._alertRemoveTimer);

        const node = document.createElement('div');
        node.setAttribute('role', 'alert');
        node.setAttribute('aria-atomic', 'true');
        node.className = 'visually-hidden';
        document.body.appendChild(node);
        this._alertNode = node;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this._alertNode === node) {
              node.textContent = message;
            }
          });
        });

        this._alertRemoveTimer = setTimeout(() => {
          if (this._alertNode === node) {
            node.remove();
            this._alertNode = null;
          }
        }, 4000);
      }

      setSuggestionsOpen(open) {
        if (!this.suggestions || !this.storeLocatorSearch) return;
        this.suggestions.classList.toggle('hidden', !open);
        this.storeLocatorSearch.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (!open) {
          this.activeSuggestionIndex = -1;
          this.storeLocatorSearch.removeAttribute('aria-activedescendant');
          clearTimeout(this._suggestAnnounceTimer);
          clearTimeout(this._optionAnnounceTimer);
          clearTimeout(this._searchLocationsTimer);
          if (this.suggestionStatus) this.suggestionStatus.textContent = '';
          if (this._alertNode) {
            this._alertNode.remove();
            this._alertNode = null;
          }
          this.suggestions.querySelectorAll('[role="option"]').forEach((option) => {
            option.setAttribute('aria-selected', 'false');
            option.setAttribute('tabindex', '-1');
          });
        }
      }

      setActiveSuggestion(index, { focusOption = false } = {}) {
        const options = [...this.suggestions.querySelectorAll('[role="option"]')];
        const isApple = this.isAppleScreenReaderHost();
        this.activeSuggestionIndex = index;
        options.forEach((option, i) => {
          const selected = i === index;
          option.setAttribute('aria-selected', selected ? 'true' : 'false');
          // Only Mac moves real focus into options; keep Windows options non-tabbable.
          option.setAttribute('tabindex', selected && focusOption && isApple ? '0' : '-1');
          option.setAttribute('aria-posinset', String(i + 1));
          option.setAttribute('aria-setsize', String(options.length));
        });

        this.storeLocatorSearch.removeAttribute('aria-activedescendant');

        if (index >= 0 && options[index]) {
          options[index].scrollIntoView({ block: 'nearest' });
          if (focusOption) {
            const option = options[index];
            const label = (option.getAttribute('aria-label') || option.textContent || '').trim();

            // Stop typing-list announcement so it does not overlap the active city.
            clearTimeout(this._suggestAnnounceTimer);
            clearTimeout(this._optionAnnounceTimer);
              if (this.suggestionStatus) this.suggestionStatus.textContent = '';

            if (isApple) {
              // VoiceOver: focus the option, then speak city (native often only says "N of M").
              option.focus({ preventScroll: true });
              if (label) {
                const message = `${label}. ${index + 1} מתוך ${options.length}`;
                this._optionAnnounceTimer = setTimeout(() => {
                  this.speakAlert(message);
                }, 150);
              }
              return;
            }

            // Windows/NVDA: APG editable combobox — focus stays on the input,
            // aria-activedescendant points at the active option (NVDA reads the city).
            this.storeLocatorSearch.setAttribute('aria-activedescendant', option.id);
            if (document.activeElement !== this.storeLocatorSearch) {
              this.storeLocatorSearch.focus({ preventScroll: true });
            }
          }
        }
      }

      handleSuggestionKeydown(event) {
        const options = [...this.suggestions.querySelectorAll('[role="option"]')];
        const listVisible = !this.suggestions.classList.contains('hidden') && options.length > 0;

        if (event.key === 'ArrowDown') {
          if (!listVisible) return;
          event.preventDefault();
          // Windows/NVDA needs expanded; Mac keeps it false to avoid "expanded list box" over city speech.
          if (!this.isAppleScreenReaderHost()) {
            this.storeLocatorSearch.setAttribute('aria-expanded', 'true');
          }
          const next = this.activeSuggestionIndex < options.length - 1
            ? this.activeSuggestionIndex + 1
            : 0;
          this.setActiveSuggestion(next, { focusOption: true });
          return;
        }

        if (event.key === 'ArrowUp') {
          if (!listVisible) return;
          event.preventDefault();
          if (!this.isAppleScreenReaderHost()) {
            this.storeLocatorSearch.setAttribute('aria-expanded', 'true');
          }
          const prev = this.activeSuggestionIndex > 0
            ? this.activeSuggestionIndex - 1
            : options.length - 1;
          this.setActiveSuggestion(prev, { focusOption: true });
          return;
        }

        if (event.key === 'Home' && listVisible) {
          event.preventDefault();
          if (!this.isAppleScreenReaderHost()) {
            this.storeLocatorSearch.setAttribute('aria-expanded', 'true');
          }
          this.setActiveSuggestion(0, { focusOption: true });
          return;
        }

        if (event.key === 'End' && listVisible) {
          event.preventDefault();
          if (!this.isAppleScreenReaderHost()) {
            this.storeLocatorSearch.setAttribute('aria-expanded', 'true');
          }
          this.setActiveSuggestion(options.length - 1, { focusOption: true });
          return;
        }

        if (event.key === 'Enter' && listVisible && this.activeSuggestionIndex >= 0) {
          event.preventDefault();
          this.selectSuggestion(options[this.activeSuggestionIndex]);
          return;
        }

        if (event.key === 'Escape' && listVisible) {
          event.preventDefault();
          event.stopPropagation();
          this.setSuggestionsOpen(false);
        }
      }

      handleSuggestionListKeydown(event) {
        const option = event.target.closest('[role="option"]');
        if (!option) return;

        const options = [...this.suggestions.querySelectorAll('[role="option"]')];
        const index = options.indexOf(option);

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          const next = index < options.length - 1 ? index + 1 : 0;
          this.setActiveSuggestion(next, { focusOption: true });
          return;
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault();
          const prev = index > 0 ? index - 1 : options.length - 1;
          this.setActiveSuggestion(prev, { focusOption: true });
          return;
        }

        if (event.key === 'Home') {
          event.preventDefault();
          this.setActiveSuggestion(0, { focusOption: true });
          return;
        }

        if (event.key === 'End') {
          event.preventDefault();
          this.setActiveSuggestion(options.length - 1, { focusOption: true });
          return;
        }

        if (event.key === 'Enter') {
          event.preventDefault();
          this.selectSuggestion(option);
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          this.setSuggestionsOpen(false);
          this.storeLocatorSearch.focus({ preventScroll: true });
          return;
        }

        // Typing while on an option returns to the search field and continues the query.
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          this.storeLocatorSearch.focus({ preventScroll: true });
          this.storeLocatorSearch.value = `${this.storeLocatorSearch.value}${event.key}`;
          this.storeLocatorSearch.dispatchEvent(new Event('input', { bubbles: true }));
          this.storeLocatorSearchOnKeyUp({ target: this.storeLocatorSearch, code: event.code || 'KeyA' });
        }
      }

      init() {
        this.setAttribute('aria-hidden', 'true');
        if (this.isOverlayDrawer && !this.hasAttribute('open')) {
          this.setAttribute('inert', '');
        }

        // update title for CTO pages
        const urlParams = new URLSearchParams(window.location.search);
        const ctoParam = urlParams.get('pdp-2.0') || urlParams.get('cto');
        if (ctoParam) {
          const $myStoreLocatorDrawerHeading = document.getElementById('MyStoreLocatorDrawerHeading');
          if ($myStoreLocatorDrawerHeading?.dataset?.availabilityTitle && $myStoreLocatorDrawerHeading?.dataset?.availabilityTitle !== '') {
            $myStoreLocatorDrawerHeading.innerText = $myStoreLocatorDrawerHeading.dataset.availabilityTitle;
          }
        }

        if (urlParams.get('exploreEilat') === 'true' && this.exploreEilatBtn) {
          this.scheduleExploreEilat();
        }

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.searchLocations(true);
        });

        this.resultsDistance.addEventListener('change', () => {
          this.searchLocations(true);
        });
        // WD - additional filters as checkboxes (service filters scoped per drawer instance)
        const wdFilters = this.querySelectorAll('.wd-custom-filters.wd-store-locator .fieldset');
        if (wdFilters && wdFilters.length > 0) {
          wdFilters.forEach((fieldset)=>{
            fieldset.querySelector('input').addEventListener('change',()=>{
              this.searchLocations(true);
            })
          })
        }
        const wdFiltersLabels = this.querySelectorAll('.wd-accordion-content .fieldset label');
        if (wdFiltersLabels && wdFiltersLabels.length > 0) {
          wdFiltersLabels.forEach((label)=>{
            label.addEventListener('keydown', (event) => {
              if (event.code.toUpperCase() === 'ENTER') {
                label.click();
              }
            })
          })
        }

        // WD - the branch list is a radio group: arrows move the selection, Enter/Space
        // opens and closes the branch details without dropping the selection.
        this.results.setAttribute('role', 'radiogroup');
        this.results.setAttribute('aria-labelledby', 'MyStoreLocatorDrawerHeading');

        this.results.addEventListener('click', (event) => {
          const result = event.target.closest('.js-my-location-result');
          if (!result || !result.dataset.id) return;
          this.selectResult(result, true);
        });

        this.results.addEventListener('keydown', (event) => {
          const result = event.target.closest('.js-my-location-result');
          if (!result || !result.dataset.id) return;

          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            const willExpand = result.dataset.expanded !== 'true';
            this.selectResult(result, willExpand);
            wdAnnounceExpanded(willExpand, result);
            return;
          }

          const results = [...this.results.querySelectorAll('.js-my-location-result')];
          const index = results.indexOf(result);
          let target = null;
          if (event.key === 'ArrowDown') target = results[index + 1];
          else if (event.key === 'ArrowUp') target = results[index - 1];
          else if (event.key === 'Home') target = results[0];
          else if (event.key === 'End') target = results[results.length - 1];
          if (!target) return;

          event.preventDefault();
          target.focus();
          this.selectResult(target, false);
        });
        // WD-end

        this._wdOnEilatModeChanged = () => {
          if (this.hasAttribute('open')) this.searchLocations(true);
        };
        window.addEventListener('wd:eilat-mode-changed', this._wdOnEilatModeChanged);

        this.storeLocatorSearch.onkeyup = debounce(this.storeLocatorSearchOnKeyUp.bind(this), 200);

        // Hide suggestions when focus leaves the search field, but not while a
        // screen-reader announcement is still in flight (blur races with VO/ChromeVox).
        this.storeLocatorSearch.addEventListener('blur', () => {
          setTimeout(() => {
            if (document.activeElement === this.storeLocatorSearch) return;
            if (this.suggestions.contains(document.activeElement)) return;
            this.setSuggestionsOpen(false);
          }, 250);
        });
      }

      async storeLocatorSearchOnKeyUp(e) {
        const navKeys = ['ARROWDOWN', 'ARROWUP', 'ARROWLEFT', 'ARROWRIGHT', 'ESCAPE', 'ENTER', 'TAB', 'HOME', 'END'];
        if (navKeys.includes(e.code.toUpperCase())) {
          return;
        }

        const userData = e.target.value;
        let emptyArray = [];
        if (userData) {
          emptyArray = await this.getPlaceSuggestions(userData);
          if (emptyArray.length > 0) {
            const suggestionLabels = emptyArray.map((data) => (
              typeof data === 'object' ? data.title : data
            ));
            const suggestionsHtml = emptyArray.map((data, index) => {
              const title = typeof data === 'object' ? data.title : data;
              const safeTitle = String(title)
                .replace(/&/g, '&amp;')
                .replace(/"/g, '&quot;')
                .replace(/</g, '&lt;');
              if (typeof data === 'object') {
                return `<div role="option" tabindex="-1" id="storeLocatorSuggestion-${index}" aria-selected="false" aria-label="${safeTitle}" data-latitude="${data.latitude}" data-longitude="${data.longitude}">
                  ${safeTitle}
                </div>`;
              }

              return `<div role="option" tabindex="-1" id="storeLocatorSuggestion-${index}" aria-selected="false" aria-label="${safeTitle}">${safeTitle}</div>`;
            });
            this.suggestions.innerHTML = suggestionsHtml.join('');
            this.setActiveSuggestion(-1);

            if (this.isAppleScreenReaderHost()) {
              // Mac/VoiceOver: show the dropdown visually but keep aria-expanded false
              // so VO does not say "expanded, list box, N items" over the city list speech.
              this.suggestions.classList.remove('hidden');
              this.storeLocatorSearch.setAttribute('aria-expanded', 'false');
            } else {
              // Windows/NVDA: real expanded combobox + aria-live announcement.
              this.setSuggestionsOpen(true);
            }
            this.announceSuggestionList(suggestionLabels);

            clearTimeout(this._searchLocationsTimer);
            this._searchLocationsTimer = setTimeout(() => {
              this.searchLocations(true);
            }, 900);
            return;
          } else {
            this.setSuggestionsOpen(false);
          }
        } else {
          this.setSuggestionsOpen(false);
        }
        this.searchLocations(true);
      }

      localizeUrl($url) {
        const rootWithoutEndingSlash = Shopify.routes.root.slice(0, -1);
        const urlParts = $url.split('/');
        urlParts[0] = rootWithoutEndingSlash;
        return urlParts.join('/');
      }

      getPlaceSuggestions(address) {
        const apiProvider = SDG?.Data?.mapProvider || null;
        const countriesLimit = this.storeLocator.dataset.limitSearchToCountries;
        if (apiProvider === 'google_maps') {
          return new Promise((resolve, reject) => {
            const componentRestrictions = countriesLimit !== '' ? {
              country: countriesLimit.split(',')
            } : null;
            const autocompleteService = new google.maps.places.AutocompleteService();
            const request = {
              input: address,
              types: ["(regions)"],
              componentRestrictions,
            };

            autocompleteService.getPlacePredictions(request, (predictions, status) => {
              if (status === google.maps.places.PlacesServiceStatus.OK) {
                const suggestions = predictions.map((prediction) => prediction.description);
                resolve(suggestions);
              } else {
                resolve([]);
              }
            });
          });
        } else if (apiProvider === 'apple_maps') {
          return new Promise((resolve, reject) => {
            const limitToCountries = countriesLimit !== '' ? countriesLimit : null;
            const search = new mapkit.Search({
              getsUserLocation: true,
              includeAddresses: true,
              includePointsOfInterest: false,
              includeQueries: false,
              limitToCountries,
            });
            search.autocomplete(address, (error, data) => {
              if (!error) {
                const suggestions = data?.results?.map((result) => {
                  return {
                    title: result.displayLines[0],
                    latitude: result.coordinate.latitude,
                    longitude: result.coordinate.longitude,
                  }
                });
                resolve(suggestions);
              } else {
                resolve([]);
              }
            });
          });
        }
      }

      /** Check a branch in the radio group and set whether its details are expanded. */
      selectResult(result, expanded) {
        const previous = this.querySelector(this.dom.activeResult);
        if (previous && previous !== result) {
          previous.dataset.active = 'false';
          previous.dataset.expanded = 'false';
          previous.setAttribute('aria-checked', 'false');
          previous.setAttribute('aria-expanded', 'false');
        }

        // Roving tabindex: only the selected branch stays in the tab order, so Tab leaves
        // the list for the select button instead of walking every branch.
        this.results.querySelectorAll('.js-my-location-result').forEach((item) => {
          item.setAttribute('tabindex', item === result ? '0' : '-1');
        });

        result.dataset.active = 'true';
        result.dataset.expanded = expanded ? 'true' : 'false';
        result.setAttribute('aria-checked', 'true');
        result.setAttribute('aria-expanded', expanded ? 'true' : 'false');

        // Screen readers ignore aria-expanded on role="radio" (it is not a supported
        // state for that role), so the open/closed state is also folded into the
        // accessible name. That way it is reported whenever the branch is announced,
        // which does not depend on a live region winning a race against other speech.
        wdSetBranchStateInName(previous, false);
        wdSetBranchStateInName(result, expanded);

        this.selectBtn.disabled = result.dataset.checkoutEnabled === 'false';
        this.updateSelectBtnA11y();
      }

      updateSelectBtnA11y() {
        if (!this.selectBtn) return;
        const activeResult = this.querySelector(this.dom.activeResult);
        const locationName = activeResult?.dataset?.name?.trim();
        const label = locationName
          ? `${this.selectBtnLabel}, ${locationName}`
          : this.selectBtnLabel;
        this.selectBtn.setAttribute('aria-label', label);
      }

      selectPreferredLocation() {
        const activeResult = this.querySelector(this.dom.activeResult);
        if (activeResult) {
          const id = activeResult.dataset.id;
          if (this.storeLocator.allLocationsData) {
            const location = this.storeLocator.allLocationsData.find((location) => location.id === id);
            if (location) {
              if (this.storeLocator.storageAvailable('localStorage')) {
                localStorage.setItem('preferredStoreId', location.id);
              }
              this.storeLocator.updateMyStoreWithLocation(location);
              // reset the drawer
              this.searchLocations(true);
              this.hide();
            }
          }
        }
      }

      useLocation() {
        this.storeLocatorSearch.value = '';
        this.loading.classList.remove('hidden');
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            this.updateDrawerStoreDetails.bind(this),
            () => {
              // A real, current failure: nothing is written to the location cookies.
              this.updateDrawerStoreDetails();
              if (window.location.pathname === '/pages/store-locator') {
                alert('Location position is not valid. Please check your location settings.');
              }
            }
          );
        } else {
          this.updateDrawerStoreDetails();
        }
      }

      /** Fill search with Eilat, set 5km radius, and show nearby Eilat stores. */
      exploreEilatStores(updateUrl = false) {
        const eilatSearch = 'אילת, ישראל';
        const eilatRadiusKm = '5';

        this.storeLocatorSearch.value = eilatSearch;
        this.setSuggestionsOpen(false);

        if (this.resultsDistance) {
          const hasRadiusOption = [...this.resultsDistance.options].some(
            (option) => option.value === eilatRadiusKm
          );
          if (!hasRadiusOption) {
            const option = document.createElement('option');
            option.value = eilatRadiusKm;
            option.textContent = `${eilatRadiusKm} ק״מ`;
            this.resultsDistance.appendChild(option);
          }
          this.resultsDistance.value = eilatRadiusKm;
        }

        const distanceRadio = this.querySelector(
          `input[name="distance_of_store"][value="${eilatRadiusKm}"]`
        );
        if (distanceRadio) {
          distanceRadio.checked = true;
        }

        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('exploreEilat', 'true');
          history.replaceState({}, '', url);
        }

        this.searchLocations(true);
      }

      /** Wait for maps + locations, then apply exploreEilat from the URL. */
      scheduleExploreEilat() {
        this.loading?.classList.remove('hidden');

        let attempts = 0;
        const tryApply = () => {
          attempts += 1;
          const mapsReady =
            (typeof google !== 'undefined' && google.maps?.Geocoder) ||
            (typeof mapkit !== 'undefined' && mapkit.Geocoder);
          const locationsReady = Boolean(this.storeLocator?.allLocationsData?.length);

          if (mapsReady && locationsReady) {
            this.exploreEilatStores(false);
            return;
          }

          if (attempts < 50) {
            setTimeout(tryApply, 200);
          } else {
            this.loading?.classList.add('hidden');
          }
        };
        tryApply();
      }

      updateDrawerStoreDetails(position) {
        const coords = this.storeLocator.getValidCoords(position);
        if (coords) {
          // A raw GeolocationPosition keeps its fields on the prototype, so JSON.stringify
          // can give "{}" — store plain numbers, like init() does.
          this.storeLocator.setSecureCookie('location_sharing', 'true', this.storeLocator.expiry);
          this.storeLocator.setSecureCookie('location_position', JSON.stringify({ coords }), this.storeLocator.expiry);
        }
        this.storeLocator.fetchLocations(position, false, () => {
          this.loading.classList.add('hidden');
        });
      }

      selectSuggestion(element){
        let selectData = element.textContent;
        this.storeLocatorSearch.value = selectData.trim();
        this.setSuggestionsOpen(false);
        this.storeLocatorSearch.focus({ preventScroll: true });
        this.searchLocations(false, element.dataset);
      }

      /** Lowercase and drop niqqud, geresh/gershayim, quotes and punctuation so Hebrew matching ignores spelling noise. */
      normalizeSearchText(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[\s,.\-–—/()\u05BE]+/g, ' ')
          .replace(/[\u0591-\u05C7]/g, '')
          .replace(/["'`\u05F3\u05F4]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }

      /**
       * 0 when the branch does not match. Every word of the query must appear in the
       * name, city or address; exact / prefix hits on the city or name rank higher.
       * A single character only looks at name and city, otherwise letters common in
       * street names (e.g. "רח׳") would match nearly every branch.
       */
      getTextMatchScore(location, query) {
        const name = this.normalizeSearchText(location.name);
        const city = this.normalizeSearchText(location.address?.city);
        const address = query.length > 1 ? this.normalizeSearchText([
          location.address?.address1,
          location.address?.province,
          location.address?.zip,
        ].join(' ')) : '';
        const haystack = `${name} ${city} ${address}`;
        if (!query.split(' ').every((term) => haystack.includes(term))) return 0;

        const fieldScore = (field) => {
          if (!field) return 0;
          if (field === query) return 4;
          if (field.startsWith(query)) return 3;
          if (` ${field}`.includes(` ${query}`)) return 2;
          return field.includes(query) ? 1 : 0;
        };
        return 1 + Math.max(fieldScore(city), fieldScore(name)) * 2 + fieldScore(address);
      }

      /** Branches matching the query, best match first (ties keep the current order). */
      getTextMatches(locations, searchLocation) {
        const query = this.normalizeSearchText(searchLocation);
        if (!query) return [];
        return locations
          .map((location) => ({ location, score: this.getTextMatchScore(location, query) }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ location }) => location);
      }

      getLocationKmFrom(origin, location) {
        const from = this.storeLocator.getValidCoords({ coords: origin });
        const to = this.storeLocator.getValidCoords({ coords: location?.address });
        if (!from || !to) return null;
        return this.storeLocator.getKmFromLatLon(from.latitude, from.longitude, to.latitude, to.longitude);
      }

      /** Badge distance always keeps one decimal, e.g. "8.0 ק״מ". */
      formatBadgeDistance(km) {
        const { distanceUnit, distanceKmLabel, distanceMilesLabel } = this.storeLocator.dataset;
        if (distanceUnit === 'km') return `${km.toFixed(1)} ${distanceKmLabel}`;
        return `${(km * 0.621371).toFixed(1)} ${distanceMilesLabel}`;
      }

      /** Service / opening-hours / Eilat filters (the radius filter is applied separately). */
      passesLocationFilters(location, applyEilatNameFilter, isEilatModeForList) {
        //wd- additional filters as checkboxes - unrelated to distance; Eilat name filter (cart/product drawer only) uses outer scope
        const wdFilterLab = this.querySelector('.wdServicesFilters input[name="lab"]');
        const wdFilterExpert = this.querySelector('.wdServicesFilters input[name="expert"]');
        const wdFilterIdigitalRepair = this.querySelector('.wdServicesFilters input[name="idigital_repair"]');
        const wdFilterIconRepair = this.querySelector('.wdServicesFilters input[name="icon_repair"]');
        const wdFilterOpenLate = document.querySelector('input[name="open_late"]');
        const wdFilterOpenSaturday = document.querySelector('input[name="open_saturday"]');
        if(wdFilterLab && wdFilterLab.checked) {
          if (!location.allCustomers || location.allCustomers.value !== 'true') {
            return false;
          }
        }
        if(wdFilterExpert && wdFilterExpert.checked) {
          if (!location.idigital || location.idigital.value !== 'true') {
            return false;
          }
        }
        if(wdFilterIdigitalRepair && wdFilterIdigitalRepair.checked) {
          if (!location.isLabService || location.isLabService.value !== 'true') {
            return false;
          }
        }
        if(wdFilterIconRepair && wdFilterIconRepair.checked) {
          if (!location.isTechnicalSupport || location.isTechnicalSupport.value !== 'true') {
            return false;
          }
        }
        if(wdFilterOpenLate && wdFilterOpenLate.checked) {
          if (!location.isOpenLate || location.isOpenLate.value !== 'true') {
            return false;
          }
        }
        if(wdFilterOpenSaturday && wdFilterOpenSaturday.checked) {
          if (!location.isOpenSaturday || location.isOpenSaturday.value !== 'true') {
            return false;
          }
        }
        if (applyEilatNameFilter) {
          const locName = location.name || '';
          if (isEilatModeForList && !locName.includes(WD_EILAT_LOCATION_NAME_MARK)) return false;
          if (!isEilatModeForList && locName.includes(WD_EILAT_LOCATION_NAME_MARK)) return false;
        }
        // wd-end
        return true;
      }

      async searchLocations(noErrors = false, dataset = null) {
        let allLocationsData = this.storeLocator.allLocationsData;
        // check if any selected item locations exist
        if (this.selectedItemLocationsKey) {
          const selectedItemLocationsData = this.storeLocator.itemLocationsData[this.selectedItemLocationsKey];
          if (selectedItemLocationsData) {
            allLocationsData = selectedItemLocationsData;
          }
        }
        let searchLocation = this.storeLocatorSearch.value;
        searchLocation = searchLocation.trim();
        if (noErrors) {
          this.error.classList.add('hidden');
        } else {
          if(searchLocation === '') {
            this.error.classList.remove('hidden');
            return;
          } else {
            this.error.classList.add('hidden');
          }
        }
        this.loading.classList.add('hidden');

        // Typing fires several searches; a slow geocode must not overwrite a newer result.
        const searchId = (this._searchId || 0) + 1;
        this._searchId = searchId;

        if (!searchLocation) {
          this.updateStoreDetails(allLocationsData, searchLocation);
          return;
        }

        // WD - match branch name / city / address first
        const textMatches = this.getTextMatches(allLocationsData, searchLocation);
        if (textMatches.length > 0) {
          this.updateStoreDetails(allLocationsData, searchLocation, { matches: textMatches });
          return;
        }

        // No branch matched the text: show the branches closest to the searched address.
        // Distances are computed here instead of refetching, so the user's own location
        // (myCoords + location cookies) is not replaced by the searched address.
        let origin = null;
        if (dataset && dataset.latitude && dataset.longitude) {
          origin = {
            latitude: parseFloat(dataset.latitude),
            longitude: parseFloat(dataset.longitude),
          };
        } else {
          const geocodedPosition = await this.geocodePosition(searchLocation);
          origin = geocodedPosition?.coords || null;
        }
        if (searchId !== this._searchId) return;

        // Never auto-select an autocomplete suggestion. That was jumping the UI
        // to the first city (e.g. Eilat) while the user was still typing.
        if (!origin) {
          this.updateStoreDetails([], searchLocation);
          return;
        }
        this.updateStoreDetails(allLocationsData, searchLocation, { origin });
      }

      geocodePosition(address) {
        const apiProvider = SDG?.Data?.mapProvider  || null;
        if (apiProvider === 'google_maps') {
          return new Promise((resolve, reject) => {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
              if (status === 'OK') {
                const latitude = results?.[0]?.geometry?.location?.lat();
                const longitude = results?.[0]?.geometry?.location?.lng();
                if (latitude && longitude) {
                  const position = {
                    coords: {
                      latitude,
                      longitude,
                    },
                    timestamp: Date.now(),
                  }
                  resolve(position);
                }
              }
              resolve(null);
            });
          });
        } else if (apiProvider === 'apple_maps') {
          if (apiProvider === 'apple_maps') {
            return new Promise((resolve, reject) => {
              const geocoder = new mapkit.Geocoder();
              geocoder.lookup(address, (error, data) => {
                if (!error) {
                  const result = data?.results?.[0];
                  const latitude = result?.coordinate?.latitude;
                  const longitude = result?.coordinate?.longitude;
                  if (latitude && longitude) {
                    const position = {
                      coords: {
                        latitude,
                        longitude,
                      },
                      timestamp: Date.now(),
                    };
                    resolve(position);
                  }
                }
                resolve(null);
              });
            });
          }
        }
      }

      /**
       * @param {Array} locationData
       * @param {string} nearbyValue - the search query ('' when there is none)
       * @param {{ matches?: Array, origin?: {latitude: number, longitude: number} } | null} search
       *   matches: text-matched branches, best first; origin: geocoded address (no text match)
       */
      updateStoreDetails(locationData, nearbyValue, search = null) {
        let resultCount = 0;
        const { mapPage } = SDG.Data || false;
        const applyEilatNameFilter = wdStoreLocatorDrawerShouldFilterByEilatPage();
        const isEilatModeForList = applyEilatNameFilter ? wdStoreLocatorDrawerIsEilatMode() : false;
        const favoriteId = this.storeLocator.preferredStoreId;

        // WD - filter, then order: best match, favorite, then the rest by distance
        const passing = locationData.filter((location) => (
          location.address && this.passesLocationFilters(location, applyEilatNameFilter, isEilatModeForList)
        ));
        const favorite = passing.find((location) => location.id === favoriteId) || null;

        // Distances are measured from the searched address, from the best text match,
        // or (no search) from the user's own location.
        let candidates = passing;
        let origin = this.storeLocator.myCoords || null;
        if (search?.matches) {
          const matchIds = new Set(search.matches.map((location) => location.id));
          const rank = (location) => search.matches.findIndex((match) => match.id === location.id);
          candidates = passing.filter((location) => matchIds.has(location.id)).sort((a, b) => rank(a) - rank(b));
          origin = candidates[0]?.address || null;
        } else if (search?.origin) {
          origin = search.origin;
        }
        const kmById = new Map(passing.map((location) => [location.id, this.getLocationKmFrom(origin, location)]));
        const byDistance = (a, b) => (kmById.get(a.id) ?? Infinity) - (kmById.get(b.id) ?? Infinity);

        this.resultsDistance.classList.toggle('hidden', !origin);
        const radius = this.resultsDistance.value !== 'all' ? parseFloat(this.resultsDistance.value) : null;
        const isInRadius = (location) => {
          const km = kmById.get(location.id);
          return radius === null || km === null || km <= radius;
        };

        // The drawer shows only the search results: text matches by relevance, or the
        // branches closest to the searched address, with no favorite pinned in.
        const isDrawerSearch = !mapPage;

        let orderedLocations;
        if (search) {
          let searchResults = candidates.filter(isInRadius);
          if (search.matches) {
            if (!isDrawerSearch) {
              searchResults = [searchResults[0], ...searchResults.slice(1).sort(byDistance)].filter(Boolean);
            }
          } else {
            searchResults = searchResults.sort(byDistance);
          }
          const [bestMatch, ...rest] = searchResults;
          orderedLocations = searchResults;
          if (!isDrawerSearch && bestMatch && favorite && favorite !== bestMatch && isInRadius(favorite)) {
            orderedLocations = [bestMatch, favorite, ...rest.filter((location) => location !== favorite)];
          }
        } else {
          const results = passing.filter(isInRadius);
          orderedLocations = favorite && results.includes(favorite)
            ? [favorite, ...results.filter((location) => location !== favorite)]
            : results;
        }

        // Favorite wins over "searched" when the best match is the favorite: the card
        // has a single badge slot, and the favorite label also explains why its
        // "make this my store" button is hidden.
        const getBadge = (location, index) => {
          if (location === favorite) return { type: 'favorite', text: SDG.Data.myStore };
          if (search && index === 0) return { type: 'searched', text: SDG.Data.searchedStore };
          const km = kmById.get(location.id);
          if (km !== null) {
            return {
              type: 'distance',
              text: (SDG.Data.storeDistanceBadge || '%DISTANCE%').replace('%DISTANCE%', this.formatBadgeDistance(km)),
            };
          }
          return null;
        };

        this.results.innerHTML = '';
        function setCityAndStateHTML(location) {
          const { address } = location || null;
          let html;

          if (!address) return;

          if (address?.city && address?.provinceCode) {
            html = `${address.city}, ${address.provinceCode}`
            return html;
          }

          if (address?.city && !address?.provinceCode) {
            html = `${address.city}`;
            return html;
          }
        }

        orderedLocations.forEach((location, index) => {
          const isActive = location.id === favoriteId;
          const badge = getBadge(location, index);
          const badgeHtml = badge
            ? `<div class="my-location-result__my-store my-location-result__my-store--${badge.type} apl-section-stores-locator-store-label">${badge.text}</div>`
            : '';
          // Store locator page: the distance badge already shows the distance, so the inline
          // text is left empty on those cards; it is also skipped for the reference branch
          // itself (0 km). The drawer hides badges and keeps its original inline distance.
          const km = kmById.get(location.id);
          const distanceText = badge?.type !== 'distance' && km ? this.storeLocator.getDistanceFromKm(km) : '';
          const isEilat = location.name.includes("אילת");
          let availabilityHtml = '';

          const ctoPickupSelector = this.selectedItemLocationsKey
            ? `pickup-availability[data-message-type][data-item-key="${this.selectedItemLocationsKey}"]`
            : 'pickup-availability[data-message-type]';
          const $ctoPickupElement = wdPickupAvailabilitiesOutsideCartDrawer(ctoPickupSelector)[0] || null;
          let overridePickupMessage = $ctoPickupElement?.dataset?.overridePickupMessage;
          const messageType = $ctoPickupElement?.dataset?.messageType;
          if (!overridePickupMessage && messageType) {
            const languageKey = SDG.Data.language?.toLowerCase() || 'en';
            const pickupMessagesJson = JSON.parse(location.pickupMessages?.value || null);
            const pickupMessages = pickupMessagesJson?.[languageKey] || pickupMessagesJson?.['en'];
            switch(messageType) {
              case 'cto':
                overridePickupMessage = pickupMessages?.cto;
                break;
              case 'preorder':
                overridePickupMessage = pickupMessages?.preOrder;
                break;
              case 'in stock':
                overridePickupMessage = pickupMessages?.inStock;
                break;
            }
          }

          if (JSON.parse(location.enableToggles?.value || null)?.checkoutEnabled === false) {
            const overrideUnavailableMessage = $ctoPickupElement?.dataset?.overrideUnavailableMessage;
            availabilityHtml = `
              <div class="my-location-result__availability">
                ${overrideUnavailableMessage ? `<span>${overrideUnavailableMessage}</span>` : this.storeLocator.dataset.unavailableText}
              </div>
            `;
          // Preorder/launch has no store stock, so skip the pickup-offered gate
          } else if (overridePickupMessage && (messageType === 'preorder' || location.productPickupOfferedAtLocation) && !(messageType === 'in stock' && !location.available)) {
            availabilityHtml = `
              <div class="my-location-result__availability">
                <div class="my-location-result__availability-time my-location-result__availability-time--overridden">${overridePickupMessage}</div>
              </div>
            `;
          } else if (location.available) {
            let stockHtml = '';

            let pickUpTimeTranslated = '';
            if (location.pickUpTime) {
              const key = location.pickUpTime;
              const languageKey = SDG.Data.language?.toLowerCase() || 'en';
              pickUpTimeTranslated = window.deliveryOptionsStrings?.[languageKey]?.[key] || location.pickUpTime;
            }
            //wedev - hide stock html by customer request, commented in case they change their mind
            // if (location.quantityAvailable === 1) {
            //   const oneStockText = this.storeLocator.dataset.onlyOneLeftHtml;
            //   stockHtml = `
            //     <div class="my-location-result__availability-stock">${oneStockText}</div>
            //   `;
            // }else if (location.quantityAvailable < 10) {
            //   const stockText = this.storeLocator.dataset.lowStockHtml.replace('{{ quantity }}', location.quantityAvailable);
            //   stockHtml = `
            //     <div class="my-location-result__availability-stock">${stockText}</div>
            //   `;
            // }
            //end of wedev - hide stock html by customer request, commented in case they change their mind
            availabilityHtml = `
              <div class="my-location-result__availability">
                <div class="my-location-result__availability-time">${!isEilat ? this.storeLocator.dataset.pickUpAvailableHtml : this.storeLocator.dataset.pickUpAvailableEilat}</div>
                ${stockHtml}
              </div>
            `;
          } else if (location.available === false) {
            
            const overrideUnavailableMessage = $ctoPickupElement?.dataset?.overrideUnavailableMessage;
            const stockAtMarlog = location.marlogStock && Number(location.marlogStock) > 0 ? Number(location.marlogStock) : null;
            const isEilat = location.name.includes("אילת");
            if(stockAtMarlog && !isEilat){
              availabilityHtml = `
                <div class="my-location-result__availability">
                  <div class="my-location-result__availability-marlog">
                    <div class="my-location-result__availability-time">  זמין לרכישה. יגיע לסניף תוך כ-2 ימי עסקים.
                    <br/>
                    נעדכן אותך כשההזמנה מוכנה.
                    </div>
                  </div>
                </div>
              `;
            }else{
              availabilityHtml = `
                <div class="my-location-result__availability">
                  ${overrideUnavailableMessage ? `<span>${overrideUnavailableMessage}</span>` : this.storeLocator.dataset.unavailableText}
                </div>
              `;
            }
          }
          let itemKeyAttr = '';
          if (location.itemKey) {
            itemKeyAttr = `data-item-key="${location.itemKey}"`;
          }
          let resultHtml;
          const { address, name } = location || null;
          const apiProvider = SDG?.Data?.mapProvider  || null;

          if (!address) return;
          const { latitude, longitude } = address || null;

          let locationImageHtml = '';
          let serviceHtml = '';
          let telephoneVal = null;
          let calloutHref = null;
          let addressHref = null;
          let url = window.location.href;

          if (location?.services_html) {
            const parser = new DOMParser();
            const servicesHtml = parser.parseFromString(location.services_html, "text/html");
            const servicesLinks = servicesHtml.querySelectorAll('a');
            let localizedServices;

            // Update url with locale
            if (servicesLinks && servicesLinks.length > 0) {
              Array.from(servicesLinks).forEach((link) => {
                const href = link.getAttribute('href');
                const slashIndex = href.indexOf('/');

                if (slashIndex === 0) {
                  const localizedUrl = this.localizeUrl(href);
                  link.setAttribute('href', localizedUrl);
                  link.classList.add('apl-section-stores-locator-store-services-link');
                }
              });

              localizedServices = servicesHtml.body.innerHTML;
            }else{
              // WD - fix for apple theme
              localizedServices = location.services_html;
            }

            if (url.includes('store-locator')) {
              serviceHtml = `
                <button class="my-location-result__services-btn js-acc-button apl-section-stores-locator-store-services-btn" type="button" aria-expanded="false" aria-controls="store-services-${location.id}">
                  <span class="underlined-text">${SDG?.Data?.storeServiceTrigger ? SDG.Data.storeServiceTrigger : 'View store services'}</span>
                  <i class="myarrow fa fa-chevron-down my-location-result__services-icon js-acc-icon" aria-hidden="true"></i>
                </button>
                <div class="my-location-result__services js-acc-details" id="store-services-${location.id}">${localizedServices}</div>
              `;
            } else {
              serviceHtml = `
                <li>
                  <button class="my-location-result__services-btn js-acc-button apl-section-stores-locator-store-services-btn" type="button" aria-expanded="false" aria-controls="store-services-${location.id}">
                    <span class="underlined-text">${SDG?.Data?.storeServiceTrigger ? SDG.Data.storeServiceTrigger : 'View store services'}</span>
                    <i class="myarrow fa fa-chevron-down my-location-result__services-icon js-acc-icon" aria-hidden="true"></i>
                  </button>
                </li>
                <li class="my-location-result__services js-acc-details" id="store-services-${location.id}">${localizedServices}</li>
              `;
            }
          }

          if (location?.image_url) {
            locationImageHtml = `
              <div class="my-location-result__image apl-section-stores-locator-store-image">
                <img src="${location.image_url}" alt="${name}" />
              </div>
            `;
          }

          if (location?.address?.phone) {
            telephoneVal = location.address.phone;
          }

          if (location?.phoneOverride?.value) {
            telephoneVal = location.phoneOverride.value;
          }

          const encodedName = encodeURIComponent(name);
          if (apiProvider === 'apple_maps') {
            const encodedAddress = encodeURIComponent(address.formatted);
            addressHref = `https://maps.apple.com/place?q=${encodedName}&address=${encodedAddress}&ll=${latitude},${longitude}`;
            calloutHref = `https://maps.apple.com/place?q=${encodedName}&address=${encodedAddress}&ll=${latitude},${longitude}`;
          }

          if (apiProvider === 'google_maps') {
            addressHref = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${latitude},${longitude}`;
            calloutHref = `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${latitude},${longitude}`;
          }

          if (mapPage) {
            resultHtml = `
            <div
              class="js-my-location-result my-location-result apl-section-stores-locator-result"
              data-active="${isActive}"
              data-favorite="${isActive}"
              ${badge ? `data-badge="${badge.type}"` : ''}
              data-id="${location.id}"
              data-name="${name}"
              data-latitude="${latitude}"
              data-longitude="${longitude}"
              role="group"
              ${itemKeyAttr}
            >
              ${badgeHtml}
              ${locationImageHtml}
              <section class="js-my-results-details">
                <div class="my-location-result__details">
                  <h3 class="my-location-result__name apl-section-stores-locator-store-name">${location.name}</h3>
                  <div class="my-store-locator__details-distance apl-section-stores-locator-store-distance">${distanceText}</div>
                </div>
                <div class="my-location-result__address my-location-result__location apl-section-stores-locator-store-address">
                  ${setCityAndStateHTML(location)}
                </div>
                <ul class="list-unstyled my-location-result__business">
                  ${location?.address?.address1 ? (
                    `<li class="my-location-result__address apl-section-stores-locator-store-address-1">
                      <span class="wd-location-info-label">${SDG.Data.storeAddress || 'Address'}: </span>
                      <a class="wd-location-geo-link" href="${calloutHref}" target="_blank" tabindex="0" aria-label="כתובת ${location.address.formatted.join(', ') || ''} פתח במפות גוגל">${location.address.formatted.join(', ')}</a>
                    </li>`
                  ) : ''}
                  ${location.hours?.value  ? (
                    `<li class="my-location-result__address apl-section-stores-locator-store-address-hours">
                      <span class="wd-location-info-label">${SDG.Data.storeHours || 'Hours'}: </span>
                      <span>${location.hours.value }</span>
                    </li>`
                  ) : ''}
                </ul>
                <div class="my-location-result__link js-my-location-link visually-hidden">
                  <a href="${calloutHref}" target="_blank" rel="noopener noreferrer">נווט לסניף</a>
                </div>
              </section>
              ${serviceHtml}
              ${location?.waze?.value || location?.googleMaps?.value ? (`
                <div class="wd-location-map-links">
                  ${location.waze?.value ?(
              `<a class="wd-location-waze" href="${location.waze.value}" aria-label="נווט עם וויז ל${location.name || ''}"> 
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                        <g clip-path="url(#clip0_428_7687)">
                          <path d="M25 10.9375C25 5.3375 20.0922 0.78125 14.0625 0.78125C8.03125 0.78125 3.125 5.3375 3.13438 11.0578C3.1375 11.0781 3.42344 13.1016 2.51094 14.1687C2.12188 14.6219 1.55625 14.8438 0.78125 14.8438C0.35 14.8438 0 15.1938 0 15.625C0 17.7906 1.60469 19.2953 4.84219 20.1719C4.75156 20.4656 4.6875 20.7703 4.6875 21.0938C4.6875 22.8172 6.08906 24.2188 7.8125 24.2188C9.53594 24.2188 10.9375 22.8172 10.9375 21.0938C10.9375 21.0672 10.9297 21.0438 10.9297 21.0172C11.9094 21.0672 12.9484 21.0938 14.0625 21.0938C14.0625 22.8172 15.4641 24.2188 17.1875 24.2188C18.9109 24.2188 20.3125 22.8172 20.3125 21.0938C20.3125 20.5203 20.1453 19.9891 19.875 19.525C22.9484 17.725 25 14.5531 25 10.9375ZM18.7172 18.3859C18.2641 18.1281 17.7469 17.9688 17.1875 17.9688C16.0453 17.9688 15.0531 18.5922 14.5094 19.5109C14.3594 19.5172 14.2125 19.5312 14.0625 19.5312C12.7406 19.5312 11.5563 19.4828 10.4391 19.4141C9.88281 18.5469 8.91562 17.9688 7.8125 17.9688C7.00625 17.9688 6.27656 18.2844 5.72187 18.7875C3.45156 18.25 2.07187 17.4281 1.68125 16.325C2.50625 16.1687 3.18125 15.7875 3.69844 15.1844C5.08594 13.5641 4.69688 10.9375 4.6875 10.9375C4.6875 6.19844 8.89219 2.34375 14.0625 2.34375C19.2328 2.34375 23.4375 6.19844 23.4375 10.9375C23.4375 14.1219 21.5328 16.9016 18.7172 18.3859Z" fill="black"/>
                          <path d="M10.9688 7.8125H10.9531C10.5219 7.8125 10.1797 8.1625 10.1797 8.59375C10.1797 9.025 10.5375 9.375 10.9688 9.375C11.4 9.375 11.75 9.025 11.75 8.59375C11.75 8.1625 11.4 7.8125 10.9688 7.8125Z" fill="black"/>
                          <path d="M17.2188 7.8125H17.2031C16.7719 7.8125 16.4297 8.1625 16.4297 8.59375C16.4297 9.025 16.7875 9.375 17.2188 9.375C17.65 9.375 18 9.025 18 8.59375C18 8.1625 17.65 7.8125 17.2188 7.8125Z" fill="black"/>
                          <path d="M18.1392 11.7377C17.7158 11.6439 17.3017 11.908 17.2048 12.3283C16.8736 13.8095 15.5814 14.8439 14.0627 14.8439C12.5455 14.8439 11.2517 13.8095 10.9189 12.3283C10.8236 11.9064 10.4064 11.647 9.98453 11.7377C9.56421 11.833 9.30015 12.2502 9.3939 12.672C9.88922 14.872 11.8095 16.4064 14.0627 16.4064C16.3173 16.4064 18.2377 14.8705 18.7314 12.672C18.8267 12.2502 18.5611 11.833 18.1392 11.7377Z" fill="black"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_428_7687">
                            <rect width="25" height="25" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </a>`
                  ): ''}
                  ${location.googleMaps?.value ?(
              `<a class="wd-location-google_maps" href="${location.googleMaps.value}" aria-label="נווט עם גוגל מפות ל${location.name || ''}"> 
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                        <path d="M20.896 5.19578C20.1368 3.6743 18.9943 2.38979 17.5917 1.481C16.0963 0.512107 14.336 0 12.501 0C10.6659 0 8.90559 0.512107 7.41023 1.481C6.00765 2.38974 4.86508 3.6743 4.10595 5.19578C3.34599 6.71892 3.00659 8.40675 3.12441 10.0767C3.2501 11.8571 3.90073 13.5743 5.00605 15.0429L12.5009 25L19.9958 15.0429C21.1011 13.5744 21.7518 11.8571 21.8774 10.0767C21.9953 8.40675 21.656 6.71897 20.896 5.19578ZM19.2023 5.16516L16.681 8.51466C16.2725 6.58196 14.5536 5.12693 12.501 5.12693C12.3978 5.12693 12.2956 5.13084 12.1942 5.13807L14.7294 1.77006C16.5812 2.29496 18.1548 3.47826 19.2023 5.16516ZM12.501 6.59177C14.0491 6.59177 15.3086 7.85124 15.3086 9.39938C15.3086 10.9475 14.0491 12.207 12.501 12.207C10.9528 12.207 9.69338 10.9475 9.69338 9.39938C9.69338 7.85124 10.9528 6.59177 12.501 6.59177ZM12.501 1.46484C12.7056 1.46484 12.9082 1.4727 13.1089 1.48715L9.52776 6.2447L6.62415 4.05911C8.09339 2.40741 10.1769 1.46484 12.501 1.46484ZM5.41669 5.84978C5.52182 5.63909 5.63534 5.43577 5.75512 5.23836L8.64685 7.41501L5.0685 12.1689C4.29233 10.1421 4.39526 7.89699 5.41669 5.84978ZM5.80531 13.6258L8.32088 10.2839C8.72932 12.2167 10.4483 13.6718 12.501 13.6718C12.6041 13.6718 12.7063 13.6679 12.8076 13.6607L9.30334 18.3161L6.17645 14.162C6.04442 13.9866 5.92133 13.8075 5.80531 13.6258ZM18.8255 14.162L12.501 22.5643L10.2201 19.534L19.929 6.63562C20.8898 9.17936 20.4992 11.9383 18.8255 14.162Z" fill="black"/>
                      </svg>
                    </a>`
                  ): ''}
                </div>`) : ''}
              ${JSON.parse(location.enableToggles?.value || null)?.checkoutEnabled !== false ? (`
                <button tabindex="0" aria-label="${location.name || ''}-${SDG.Data.makeThisMyStore}" aria-pressed="${isActive}" class="js-make-my-store-btn button button--secondary button--full-width my-location-result__make-my-store-btn apl-section-stores-locator-store-set-store">
                  ${SDG.Data.makeThisMyStore}
                </button>
              `) : ''}
            </div>`;
            const resultNode = new DOMParser().parseFromString(resultHtml, 'text/html').body.childNodes[0];
            resultNode.querySelector('.js-make-my-store-btn')?.addEventListener('click', (e) => {
              const activeResult = this.querySelector(this.dom.activeResult);
              if (activeResult) {
                activeResult.dataset.active = 'false';
              }
              e.currentTarget.parentNode.dataset.active = 'true';

              this.selectPreferredLocation();
            });

            this.results.append(resultNode);
            resultCount++;
          } else {
            resultHtml = `
            <div
              class="js-my-location-result my-location-result my-location-result--drawer apl-section-stores-locator-result"
              data-active="${isActive}"
              data-expanded="${isActive}"
              data-favorite="${isActive}"
              ${badge ? `data-badge="${badge.type}"` : ''}
              data-id="${location.id}"
              data-name="${name}"
              data-latitude="${latitude}"
              data-longitude="${longitude}"
              role="radio"
              tabindex="${isActive ? '0' : '-1'}"
              aria-label="${location.name}"
              aria-checked="${isActive}"
              aria-expanded="${isActive}"
              aria-controls="store-contact-${location.id}"
              data-checkout-enabled="${JSON.parse(location.enableToggles?.value || null)?.checkoutEnabled !== false}"
              ${itemKeyAttr}
            >
              <div class="my-location-result-details">
                ${badgeHtml}
                <section class="js-my-results-details">
                  <div class="my-location-result__details">
                    <h3 class="my-location-result__name apl-section-stores-locator-store-name">${location.name}</h3>
                    <div class="my-store-locator__details-distance apl-section-stores-locator-store-distance">${location?.distance || ''}</div>
                  </div>
                  <div class="my-location-result__address apl-section-stores-locator-store-address">
                  ${setCityAndStateHTML(location)}
                  </div>
                  ${availabilityHtml}
                </section>
              </div>
              <ul id="store-contact-${location.id}" class="list-unstyled my-location-result-details--contact">
                ${location?.address?.address1 ? (
                  `<li class="my-location-result__address apl-section-stores-locator-store-address-1">
                    <span class="wd-location-info-label">${SDG.Data.storeAddress || 'Address'}: </span>
                    <a class="wd-location-geo-link" href="${addressHref}" target="_blank" rel="noopener noreferrer" tabindex="0" aria-label="כתובת ${location.address.formatted.join(', ') || ''} פתח במפות גוגל">${location.address.formatted.join(', ')}</a>
                  </li>`
                ) : ''}
                ${location.hours?.value  ? (
                  `<li class="my-location-result__address apl-section-stores-locator-store-address-hours">
                    <span class="wd-location-info-label">${SDG.Data.storeHours || 'Hours'}: </span>
                    <span>${location.hours.value }</span>
                  </li>`
                ) : ''}
                ${serviceHtml}
                ${location.waze?.value ?(
              `<a class="wd-location-waze" href="${location.waze.value}" aria-label="נווט עם וויז ל${location.name || ''}"> 
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path d="M502.2 201.7C516.7 287.5 471.2 369.6 389 409.8c13 34.1-12.4 70.2-48.3 70.2a51.7 51.7 0 0 1 -51.6-49c-6.4 .2-64.2 0-76.3-.6A51.7 51.7 0 0 1 159 479.9c-33.9-1.4-58-34.8-47-67.9-37.2-13.1-72.5-34.9-99.6-70.8-13-17.3-.5-41.8 20.8-41.8 46.3 0 32.2-54.2 43.2-110.3C94.8 95.2 193.1 32 288.1 32c102.5 0 197.2 70.7 214.1 169.7zM373.5 388.3c42-19.2 81.3-56.7 96.3-102.1 40.5-123.1-64.2-228-181.7-228-83.5 0-170.3 55.4-186.1 136-9.5 48.9 5 131.4-68.8 131.4C58.2 358.6 91.6 378.1 127 389.5c24.7-21.8 63.9-15.5 79.8 14.3 14.2 1 79.2 1.2 87.9 .8a51.7 51.7 0 0 1 78.8-16.4zM205.1 187.1c0-34.7 50.8-34.8 50.8 0s-50.8 34.7-50.8 0zm116.6 0c0-34.7 50.9-34.8 50.9 0s-50.9 34.8-50.9 0zm-122.6 70.7c-3.4-16.9 22.2-22.2 25.6-5.2l.1 .3c4.1 21.4 29.9 44 64.1 43.1 35.7-.9 59.3-22.2 64.1-42.8 4.5-16.1 28.6-10.4 25.5 6-5.2 22.2-31.2 62-91.5 62.9-42.6 0-80.9-27.8-87.9-64.3z"/>
                    </svg>
                  </a>`
                ): ''}
                ${location.googleMaps?.value ?(
              `<a class="wd-location-google_maps" href="${location.googleMaps.value}" aria-label="נווט עם גוגל מפות ל${location.name || ''}"> 
                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0 0 50 50">
                      <path d="M 25 1 C 15.62003 1 8 8.6200302 8 18 C 8 21.934852 9.338472 25.56337 11.578125 28.441406 C 11.579015 28.442506 11.579178 28.444213 11.580078 28.445312 C 12.881845 30.20015 15.540286 33.445552 17.984375 37.027344 C 20.433903 40.617106 22.609579 44.57629 22.947266 47.255859 A 1.0001 1.0001 0 0 0 22.949219 47.269531 C 23.087696 48.259336 23.94612 49 24.939453 49 C 25.933113 49 26.813207 48.253225 26.933594 47.25 C 27.264043 44.644456 29.344744 40.842893 31.728516 37.353516 C 34.113791 33.861937 36.753391 30.641964 38.150391 28.779297 C 38.140221 28.792867 38.188921 28.726376 38.212891 28.697266 C 40.576728 25.777652 42 22.049173 42 18 C 42 8.6200302 34.37997 1 25 1 z M 25 3 C 25.609143 3 26.187453 3.1095293 26.777344 3.1796875 L 19.857422 11.634766 L 14.494141 7.2988281 C 17.198552 4.64388 20.900052 3 25 3 z M 29.009766 3.6074219 C 33.078798 4.7368158 36.415915 7.5073508 38.302734 11.193359 L 32.947266 17.740234 C 32.801586 13.457791 29.317099 10 25 10 C 24.481389 10 24.028565 10.202032 23.537109 10.296875 L 29.009766 3.6074219 z M 13.201172 8.8242188 L 18.591797 13.181641 L 10.794922 22.710938 C 10.303652 21.227167 10 19.653974 10 18 C 10 14.522139 11.223662 11.365018 13.201172 8.8242188 z M 25 12 C 28.33081 12 31 14.66919 31 18 C 31 21.33081 28.33081 24 25 24 C 21.66919 24 19 21.33081 19 18 C 19 14.66919 21.66919 12 25 12 z M 39.173828 13.285156 C 39.668281 14.775352 40 16.341297 40 18 C 40 21.586143 38.743589 24.870329 36.652344 27.449219 A 1.0001 1.0001 0 0 0 36.597656 27.525391 C 36.662996 27.427381 36.628771 27.476078 36.550781 27.580078 C 35.217781 29.357411 32.525897 32.638688 30.076172 36.224609 C 27.626447 39.81053 25.368819 43.661536 24.947266 47.005859 A 1.0001 1.0001 0 0 0 24.947266 47.011719 C 24.94765 47.008453 24.965793 47 24.939453 47 C 24.932753 47 24.931187 47.002388 24.929688 46.992188 C 24.52099 43.785616 22.399697 40.101719 20.078125 36.626953 L 39.173828 13.285156 z M 17.046875 18.230469 C 17.176564 22.527243 20.672521 26 25 26 C 25.504771 26 25.944669 25.800899 26.423828 25.710938 L 18.898438 34.910156 C 16.648702 31.73408 14.255696 28.694038 13.173828 27.234375 A 1.0001 1.0001 0 0 0 13.158203 27.214844 C 12.567692 26.456407 12.211585 25.54039 11.773438 24.675781 L 17.046875 18.230469 z"></path>
                    </svg>
                  </a>`
                ): ''}
              </ul>
            </div>`;
            const openAcc = (el) => {
              el.setAttribute('aria-expanded', true)
              el.classList.add(this.cls.active);
            }
            const closeAcc = (el) => {
              el.setAttribute('aria-expanded', false)
              el.classList.remove(this.cls.active);
            }
            const resultNode = new DOMParser().parseFromString(resultHtml, 'text/html').body.childNodes[0];

            const $accButton = resultNode?.querySelector(this.dom.acc_button) || null;
            const $accIcon = resultNode?.querySelector(this.dom.acc_icon) || null;
            const $accDetails = resultNode?.querySelector(this.dom.acc_details) || null;
            const $servicesBtn = resultNode?.querySelector('button.apl-section-stores-locator-store-services-btn') || null;
            if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'false');

            if ($accButton && $accDetails) {
              $accButton.addEventListener('click', (e) => {
                e.stopPropagation();
                if ($accDetails.classList.contains(this.cls.active)) {
                  closeAcc($accDetails);
                  if ($accIcon) closeAcc($accIcon);
                  if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'false');
                  wdAnnounceExpanded(false, $servicesBtn || $accButton);
                } else {
                  openAcc($accDetails);
                  if ($accIcon) openAcc($accIcon);
                  if ($servicesBtn) $servicesBtn.setAttribute('aria-expanded', 'true');
                  wdAnnounceExpanded(true, $servicesBtn || $accButton);
                }

                $accButton.setAttribute('data-acc', true)
              });
            }

            this.results.append(resultNode);
            resultCount++;
          }
        });
        let resultsText = '';
        if(nearbyValue === '') {
          if (this.storeLocator.myCoords) {
            if (resultCount > 1 || resultCount === 0) {
              resultsText = this.resultsCount.dataset.resultsNearYou;
            } else {
              resultsText = this.resultsCount.dataset.resultNearYou;
            }
          } else {
            if (resultCount > 1 || resultCount === 0) {
              resultsText = this.resultsCount.dataset.results;
            } else {
              resultsText = this.resultsCount.dataset.result;
            }
          }
        } else {
          if (resultCount > 1 || resultCount === 0) {
            resultsText = `${this.resultsCount.dataset.resultsNear} "${nearbyValue}"`;
          } else {
            resultsText = `${this.resultsCount.dataset.resultNear} "${nearbyValue}"`;
          }
        }
        this.resultsCount.innerHTML = `${resultCount} ${resultsText}`;
        this.resultsCount.setAttribute('aria-label', `${resultCount} ${resultsText}`);
        if (resultCount === 0) {
          this.results.innerHTML = `
            <div class="my-location-result">
              <div class="my-location-result__address apl-section-stores-locator-store-address">${this.resultsCount.dataset.noResultsFound}</div>
            </div>
          `;
        }
        this.loading.classList.add('hidden');
        this.results.scrollTop = 0;

        // With nothing selected yet the whole group would be out of the tab order.
        if (!this.results.querySelector('.js-my-location-result[tabindex="0"]')) {
          this.results.querySelector('.js-my-location-result')?.setAttribute('tabindex', '0');
        }

        this.updateSelectBtnA11y();
      }

      handleBodyClick(evt) {
        const target = evt.target;

        if (target.classList.contains('js-my-store-locator-search-drawer-launcher')) {
          return;
        }
        if (
          target !== this &&
          !target.closest('my-store-locator-drawer') &&
          !target.closest('.js-my-store-locator-btn') &&
          !target.closest('.js-my-store-locator-drawer-btn') &&
          !target.classList.contains('js-my-store-locator-btn') &&
          !target.classList.contains('js-my-store-locator-drawer-btn')
        ) {
          this.hide();
        }
        this.setSuggestionsOpen(false);
      }

      hide() {
        this.removeAttribute('open');
        this.setAttribute('aria-hidden', 'true');
        if (this.focusElement) this.focusElement.setAttribute('aria-expanded', 'false');
        document.body.removeEventListener('click', this.onBodyClick);
        document.documentElement.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
        if (this.focusElement) this.focusElement.focus();
        if (this.isOverlayDrawer) {
          this.setAttribute('inert', '');
        }
      }

      /**
       * @param {HTMLElement | null | undefined} focusElement - opener control (may be missing if invoked programmatically)
       */
      show(focusElement) {
        this.setAttribute('aria-hidden', 'false');
        if (this.isOverlayDrawer) {
          this.removeAttribute('inert');
        }

        const opener = focusElement ?? null;
        const ds = opener?.dataset;

        if (ds?.itemKey) {
          const productsInfo = document.querySelectorAll(this.dom.productInfo);
          productsInfo.forEach((productInfo) => {
            if (productInfo.dataset.itemKey === ds.itemKey) {
              productInfo.classList.remove('hidden');
            } else {
              productInfo.classList.add('hidden');
            }
          });
        } else if (ds?.productInfo === 'false') {
          const productsInfo = document.querySelectorAll(this.dom.productInfo);
          productsInfo.forEach((productInfo) => {
            productInfo.classList.add('hidden');
          });
        }

        this.setAttribute('data-hide-availability', ds?.hideAvailability || false);

        this.focusElement = opener;
        if (opener) opener.setAttribute('aria-expanded', 'true');
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.documentElement.classList.add('overflow-hidden');
        trapFocus(this);

        if (typeof window.Eilat_handler?.getCart === 'function') {
          void window.Eilat_handler.getCart().then(() => {
            if (!this.hasAttribute('open')) return;
            this.searchLocations(true);
          });
        }
      }
    }
  );
}
